import React from "react";

import { OMNICINE_AI_ANNOTATION_SUGGESTIONS_EVENT, queueAiAnnotationSuggestions, runAiProposalBridge } from "@/ai/bridge/proposalBridge";
import { useDirectorCompilerReadOnly } from "@/components/OmniCine/Inspector/directorspanel/useDirectorCompilerReadOnly";
import {
  OMNICINE_VIDEO_ANNOTATE_ADD_MARKER_EVENT,
  emitVideoAnnotateAddMarker,
} from "@/contracts/omnicine/TimelineSurfaceEvents";
import { emitOpsMetric, emitOpsTimeline } from "@/contracts/ops/emitOps";
import { useSpeechInput } from "@/hooks/useSpeechInput";
import {
  consumePendingOmniCineAnnotationDraftHandoff,
  onOmniCineAnnotationDraftHandoff,
  type OmniCineAnnotationDraftHandoffDetail,
} from "@/lib/omnistudio/bridge/omnistudioBridgeContract";
import { useOmniCineTimeline } from "@/state/useOmniCineTimeline";

import AnnotationReviewList from "../Annotations/AnnotationReviewList";
import AnnotationSurface from "../Annotations/AnnotationSurface";
import {
  buildAnnotationUpdatePatch,
  materializeSuggestionDraft,
  mergeSuggestionIntoDraft,
  resolveCanonicalAnnotationRecord,
  suggestionActionLabel,
  type AnnotationSuggestionContext,
} from "../Annotations/annotationDraft.helpers";
import type {
  AnnotationDraftHandoffContext,
  AnnotationCaptureSource,
  AnnotationRecord,
  AnnotationSuggestion,
  AnnotationSuggestionViewState,
  AnnotationSurfaceDraft,
  AnnotationSurfaceKind,
  AnnotationSurfaceValidationState,
} from "../Annotations/annotationSurface.types";
import { inferAnnotationKindFromVoice, parseAnnotationVoiceInput } from "../Annotations/annotationSurface.voice";

import NarrativeSonicsLensTags from "./NarrativeSonicsLensTags";

function normalizeSuggestionDisposition(
  value: string | null | undefined,
): AnnotationSuggestionContext["suggestionDisposition"] {
  return value === "merged" || value === "tweaked" ? value : "accepted";
}
import MediaBindingControls from "./MediaBindingControls";
import type { BindingMode, MediaBindingFamilyOption, MediaMember } from "./MediaUnion";

export type SonicsFocusScope = "source" | "clip" | "sequence";

export type SonicsLensId =
  | "arc"
  | "fx"
  | "continuity"
  | "aiPreview"
  | "audit"
  | "motif"
  | "pulse";

export interface AudioWitnessUiState {
  activeCapsuleId?: string;
  witnessBarVisible: boolean;
  activeLensId: SonicsLensId | null;
  pinnedMiniLensId: SonicsLensId | null;
  focusScope: SonicsFocusScope;
}

export const defaultAudioWitnessUiState: AudioWitnessUiState = {
  activeCapsuleId: undefined,
  witnessBarVisible: false,
  activeLensId: null,
  pinnedMiniLensId: null,
  focusScope: "source",
};

export interface AudioWitnessBarProps {
  visible: boolean;
  focusScope: SonicsFocusScope;
  title: string;
  subtitle?: string;
  mediaType: "audio" | "video" | "image";
  activeMediaUrl?: string;
  activeSourceId?: string;
  activeSourceLabel?: string;
  activeClipId?: string;
  activeClipLabel?: string;
  activeSequenceLabel?: string;
  status: "idle" | "ready" | "playing" | "previewing" | "muted" | "error";
  currentTimeSec?: number;
  durationSec?: number;
  auditionMode?: SonicsFocusScope;
  bindingMode?: BindingMode;
  mediaFamilyLabel?: string;
  hasLinkedAudio?: boolean;
  hasLinkedVideo?: boolean;
  emotionalSummary?: string;
  activeLensId?: SonicsLensId | null;
  pinnedMiniLensId?: SonicsLensId | null;
  availableLensIds: SonicsLensId[];
  hasExplicitBinding?: boolean;
  canBindCurrentFamily?: boolean;
  familyOptions?: MediaBindingFamilyOption[];
  selectedFamilyId?: string;
  audioMembers?: MediaMember[];
  videoMembers?: MediaMember[];
  selectedAudioMemberId?: string;
  selectedVideoMemberId?: string;
  onSetAuditionMode?: (mode: SonicsFocusScope) => void;
  onBindCurrentFamily?: () => void;
  onClearExplicitBinding?: () => void;
  onSelectFamily?: (familyId: string) => void;
  onSelectAudioMember?: (memberId: string) => void;
  onSelectVideoMember?: (memberId: string) => void;
  onInspectMediaMember?: (member: MediaMember, trackKind: "audio" | "video") => void;
  onToggleLens: (lensId: SonicsLensId) => void;
  onPinMiniLens: (lensId: SonicsLensId) => void;
  onRemoveActiveClip?: () => void;
  onClose: () => void;
}

export interface ClipFocusLensOverlayProps {
  visible: boolean;
  activeLensId?: SonicsLensId | null;
  pinnedMiniLensId?: SonicsLensId | null;
  focusScope: SonicsFocusScope;
  status: "idle" | "ready" | "playing" | "previewing" | "muted" | "error";
  currentTimeSec?: number;
  durationSec?: number;
  activeSourceId?: string;
  activeClipId?: string;
  placement?: "aboveBar" | "stage";
}

const AUDIO_WITNESS_MAX_HEIGHT_PX = 176;
const ANNOTATION_PANEL_MAX_VIEWPORT_HEIGHT = "min(68vh, calc(100dvh - 8rem))";
const OMNICINE_TOAST_EVENT = "omnicine:toast";
const OMNICINE_EXPORT_EVENT = "omnicine:export";
const OMNICINE_EXPORT_RESULT_EVENT = "omnicine:export:result";
const OMNICINE_VIDEO_SHARE_EVENT = "omnicine:video:share";
const OMNICINE_VIDEO_SHARE_RESULT_EVENT = "omnicine:video:share:result";
const OMNICINE_VIDEO_FRAME_CAPTURE_EVENT = "omnicine:video:frame:capture";
const OMNICINE_VIDEO_FRAME_CAPTURE_RESULT_EVENT = "omnicine:video:frame:capture:result";
const OMNICINE_VIDEO_ANNOTATE_EVENT = "omnicine:video:annotate";
const OMNICINE_ANNOTATION_SURFACE_OPEN_EVENT = "omnicine:annotationSurface:open";
const OMNICINE_ANNOTATION_SURFACE_TRACE_EVENT = "omnicine:annotationSurface:trace";

const VALID_ANNOTATION_KINDS: AnnotationSurfaceKind[] = ["creative", "continuity", "timing", "audio", "npc", "intent", "publish"];
const OMNICINE_VIDEO_EXPORT_EVENT = "omnicine:video:export";
const OMNICINE_VIDEO_EXPORT_RESULT_EVENT = "omnicine:video:export:result";
const OMNICINE_VIDEO_PUBLISH_EVENT = "omnicine:video:publish";
const OMNICINE_VIDEO_PUBLISH_RESULT_EVENT = "omnicine:video:publish:result";
const VIDEO_WITNESS_CAPABILITY_ID = "omnicine.timeline.videoWitnessBar" as const;

type OmniCineToastKind = "info" | "success" | "warning" | "error";

function fmt(sec: number | undefined): string {
  const safe = typeof sec === "number" && Number.isFinite(sec) ? Math.max(0, sec) : 0;
  const mm = Math.floor(safe / 60);
  const ss = Math.floor(safe % 60)
    .toString()
    .padStart(2, "0");
  return `${mm}:${ss}`;
}

type VideoActionPanelId = "share" | "frame" | "export" | "publish" | "annotate";

function focusScopeLabel(focusScope: SonicsFocusScope): string {
  switch (focusScope) {
    case "clip":
      return "Clip Focus";
    case "sequence":
      return "Sequence Focus";
    default:
      return "Source Focus";
  }
}

function copyableTimecode(sec: number | undefined): string {
  return fmt(sec);
}

function formatAnnotationTimecode(sec: number | undefined, fps = 30): string {
  const safeSec = typeof sec === "number" && Number.isFinite(sec) ? Math.max(0, sec) : 0;
  const safeFps = Math.max(1, Math.floor(fps || 30));
  const wholeSeconds = Math.floor(safeSec);
  const hours = Math.floor(wholeSeconds / 3600);
  const minutes = Math.floor((wholeSeconds % 3600) / 60);
  const seconds = wholeSeconds % 60;
  const frames = Math.min(safeFps - 1, Math.floor((safeSec - wholeSeconds) * safeFps + 1e-4));

  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}:${frames.toString().padStart(2, "0")}`;
}

function annotationFrameIndex(sec: number | undefined, fps = 30): number {
  const safeSec = typeof sec === "number" && Number.isFinite(sec) ? Math.max(0, sec) : 0;
  const safeFps = Math.max(1, Math.floor(fps || 30));
  return Math.max(0, Math.floor(safeSec * safeFps));
}

function compactMediaLabel(label?: string): string {
  const safe = String(label ?? "").trim();
  if (!safe) return "media";
  return safe.length > 28 ? `${safe.slice(0, 25).trimEnd()}...` : safe;
}

function annotationKindLabel(kind: AnnotationSurfaceKind): string {
  switch (kind) {
    case "continuity":
      return "Continuity";
    case "timing":
      return "Timing";
    case "audio":
      return "Audio";
    case "npc":
      return "NPC";
    case "intent":
      return "Intent";
    case "publish":
      return "Publish";
    default:
      return "Creative";
  }
}

function emitAnnotationSurfaceTrace(action: string, detail: Record<string, string | number | boolean | null | undefined>) {
  emitVideoOpsTimeline(`annotation_surface_${action}`, detail);
  emitWindowEvent(OMNICINE_ANNOTATION_SURFACE_TRACE_EVENT, { action, ...detail });
}

function classifyAnnotationPanelPhase(
  previousState: { open: VideoActionPanelId | null; presence: VideoActionPanelId | null },
  nextState: { open: VideoActionPanelId | null; presence: VideoActionPanelId | null },
): "steady_closed" | "steady_open" | "opening" | "closing" | "unexpected_divergence" {
  const annotateOpen = nextState.open === "annotate";
  const annotatePresent = nextState.presence === "annotate";

  if (annotateOpen && annotatePresent) {
    return "steady_open";
  }

  if (!annotateOpen && !annotatePresent) {
    return "steady_closed";
  }

  if (annotateOpen && !annotatePresent) {
    return "opening";
  }

  if (
    !annotateOpen
    && annotatePresent
    && (previousState.open === "annotate" || previousState.presence === "annotate")
  ) {
    return "closing";
  }

  return "unexpected_divergence";
}

function mergeSuggestionQueue(
  prev: AnnotationSuggestion[],
  next: AnnotationSuggestion[],
  replaceExisting = false,
): AnnotationSuggestion[] {
  const base = replaceExisting ? [] : prev.slice();
  const byId = new Map(base.map((item) => [item.id, item]));

  for (const suggestion of next) {
    byId.set(suggestion.id, suggestion);
  }

  return Array.from(byId.values()).sort((left, right) => (left.timeSec ?? 0) - (right.timeSec ?? 0));
}

function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  const tagName = target.tagName.toLowerCase();
  return tagName === "input" || tagName === "textarea" || tagName === "select";
}

function emitWindowEvent(type: string, detail: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(type, { detail }));
}

function emitOmniCineToast(kind: OmniCineToastKind, title: string, message: string) {
  emitWindowEvent(OMNICINE_TOAST_EVENT, { kind, title, message });
}

function emitVideoOpsMetric(name: string, band: "normal" | "warning" = "normal", value = 1, channel?: string) {
  emitOpsMetric({
    v: 1,
    at: Date.now(),
    kind: "custom",
    name,
    value,
    band,
    capabilityId: VIDEO_WITNESS_CAPABILITY_ID,
    ...(channel ? { channel } : {}),
  });
}

function emitVideoOpsTimeline(action: string, detail: Record<string, string | number | boolean | null | undefined>) {
  emitOpsTimeline({
    v: 1,
    at: Date.now(),
    kind: "ops_action",
    detail: {
      action,
      capabilityId: VIDEO_WITNESS_CAPABILITY_ID,
      ...detail,
    },
  });
}

function slugifyLabel(label: string): string {
  const normalized = String(label).trim().toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return normalized.replace(/^-+|-+$/g, "") || "omnicine-media";
}

function makeRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `oc-export-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function inferExtensionFromUrl(url?: string | null, fallback = "mp4"): string {
  if (!url) return fallback;

  try {
    const parsed = new URL(url, window.location.href);
    const match = /\.([a-z0-9]{2,5})$/i.exec(parsed.pathname);
    return match ? match[1].toLowerCase() : fallback;
  } catch {
    return fallback;
  }
}

function downloadBlob(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  link.rel = "noopener";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 2500);
}

function downloadUrl(url: string, filename: string) {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function findActiveVideoElement(): HTMLVideoElement | null {
  if (typeof document === "undefined") return null;

  const selectors = [
    "[data-oc-media-panel-mode='focus'] video",
    "[data-oc-media-panel-mode='overlay'] video",
  ];

  for (const selector of selectors) {
    const node = document.querySelector(selector);
    if (node instanceof HTMLVideoElement) return node;
  }

  return null;
}

async function captureVideoFrameBlob(video: HTMLVideoElement): Promise<Blob> {
  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || video.videoWidth <= 0 || video.videoHeight <= 0) {
    throw new Error("Video frame is not ready yet.");
  }

  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("Frame capture canvas is unavailable.");

  context.drawImage(video, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/png");
  });

  if (!blob) throw new Error("Could not encode the current video frame.");
  return blob;
}

function modeTickerText(mode: SonicsFocusScope, mediaLabel: string, sequenceLabel?: string): string {
  switch (mode) {
    case "source":
      return `SOURCE · reuse ${mediaLabel}`;
    case "sequence":
      return `SEQUENCE · full context`;
    default:
      return `CLIP · local edit`;
  }
}

function bindingTickerText(bindingMode: BindingMode | undefined, hasLinkedAudio: boolean, hasLinkedVideo: boolean): string {
  const linkSummary = hasLinkedAudio && hasLinkedVideo ? "A/V" : hasLinkedAudio ? "audio" : hasLinkedVideo ? "video" : "none";
  const modeLabel = String(bindingMode ?? "unbound").toUpperCase();
  return `${modeLabel} · ${linkSummary}`;
}

function compactArcAuditionSummary(auditionMode: SonicsFocusScope | undefined, mediaFocusLabel: string): string {
  if (auditionMode === "sequence") return "SEQ";
  if (auditionMode === "clip") return "CLIP";
  if (auditionMode === "source") return "SOURCE";
  return mediaFocusLabel.replace(/\s+Focus$/i, "").toUpperCase();
}

function compactArcModeSummary(mode: SonicsFocusScope): string {
  switch (mode) {
    case "sequence":
      return "SEQ";
    case "source":
      return "SOURCE";
    default:
      return "LOCAL";
  }
}

function compactArcBindingSummary(bindingMode: BindingMode | undefined, hasLinkedAudio: boolean, hasLinkedVideo: boolean): string {
  const route = hasLinkedAudio && hasLinkedVideo ? "A/V" : hasLinkedAudio ? "AUD" : hasLinkedVideo ? "VID" : "NONE";
  const bind = String(bindingMode ?? "unbound").trim().toUpperCase();
  return `${bind} ${route}`;
}

function lensMeta(lensId: SonicsLensId | null) {
  switch (lensId) {
    case "arc":
      return {
        label: "ARC",
        title: "Emotional Arc",
        accent: "rgba(45, 212, 191, 0.92)",
        border: "rgba(45, 212, 191, 0.22)",
        glow: "rgba(20, 184, 166, 0.18)",
        tint: "rgba(45, 212, 191, 0.16)",
        sheen: "rgba(153, 246, 228, 0.16)",
        summary: "Arc tone is etched into the focus surface so the emotional contour reads as context, not as a second panel.",
        pills: ["Valence", "Intensity", "Transition"],
      };
    case "fx":
      return {
        label: "FX",
        title: "FX Intent",
        accent: "rgba(56, 189, 248, 0.92)",
        border: "rgba(56, 189, 248, 0.22)",
        glow: "rgba(14, 165, 233, 0.18)",
        tint: "rgba(56, 189, 248, 0.16)",
        sheen: "rgba(125, 211, 252, 0.16)",
        summary: "FX intent sits in the glass as a quick musical readout rather than opening a heavy utility tray.",
        pills: ["Swell", "Duck", "Lift"],
      };
    case "continuity":
      return {
        label: "CONT",
        title: "Continuity",
        accent: "rgba(251, 191, 36, 0.92)",
        border: "rgba(251, 191, 36, 0.22)",
        glow: "rgba(245, 158, 11, 0.18)",
        tint: "rgba(251, 191, 36, 0.16)",
        sheen: "rgba(253, 224, 71, 0.16)",
        summary: "Continuity status stays quiet and readable, giving carry-over context without competing with transport controls.",
        pills: ["Carry", "Flow", "Warnings"],
      };
    case "aiPreview":
      return {
        label: "AI",
        title: "AI Preview",
        accent: "rgba(192, 132, 252, 0.92)",
        border: "rgba(192, 132, 252, 0.22)",
        glow: "rgba(168, 85, 247, 0.18)",
        tint: "rgba(192, 132, 252, 0.16)",
        sheen: "rgba(216, 180, 254, 0.16)",
        summary: "AI preview swaps into the same etched field so alternate suggestions feel integrated into the instrument surface.",
        pills: ["Preview", "Branch", "Review"],
      };
    default:
      return null;
  }
}

type AnnotationUiState = {
  draft: string;
  actionDraft: string;
  kind: AnnotationSurfaceKind;
  editingAnnotationId: string | null;
  handoffContext: AnnotationDraftHandoffContext | null;
  sourceOverride: AnnotationCaptureSource | null;
  aiSuggestions: AnnotationSuggestion[];
  aiStatus: string | null;
  editingSuggestionId: string | null;
  suggestionContext: AnnotationSuggestionContext | null;
  voiceActive: boolean;
  voiceInterim: string;
  voiceFinal: string | null;
};

type AnnotationUiAction =
  | { type: "reset" }
  | { type: "patch"; patch: Partial<AnnotationUiState> };

function createInitialAnnotationUiState(): AnnotationUiState {
  return {
    draft: "",
    actionDraft: "",
    kind: "creative",
    editingAnnotationId: null,
    handoffContext: null,
    sourceOverride: null,
    aiSuggestions: [],
    aiStatus: null,
    editingSuggestionId: null,
    suggestionContext: null,
    voiceActive: false,
    voiceInterim: "",
    voiceFinal: null,
  };
}

function annotationUiReducer(state: AnnotationUiState, action: AnnotationUiAction): AnnotationUiState {
  switch (action.type) {
    case "reset":
      return createInitialAnnotationUiState();
    case "patch": {
      const hasChanges = Object.entries(action.patch).some(([key, value]) => state[key as keyof AnnotationUiState] !== value);
      return hasChanges ? { ...state, ...action.patch } : state;
    }
    default:
      return state;
  }
}

function resolveStateValue<T>(currentValue: T, nextValue: React.SetStateAction<T>): T {
  return typeof nextValue === "function"
    ? (nextValue as (previousValue: T) => T)(currentValue)
    : nextValue;
}

function usePrefersLightMode(): boolean {
  const [prefersLightMode, setPrefersLightMode] = React.useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-color-scheme: light)").matches;
  });

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: light)");
    const apply = () => setPrefersLightMode(mediaQuery.matches);
    apply();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", apply);
      return () => mediaQuery.removeEventListener("change", apply);
    }

    mediaQuery.addListener(apply);
    return () => mediaQuery.removeListener(apply);
  }, []);

  return prefersLightMode;
}

function useMountedRef() {
  const mountedRef = React.useRef(false);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return mountedRef;
}

function usePresenceMotion(active: boolean): boolean {
  const [entered, setEntered] = React.useState(false);
  const mountedRef = useMountedRef();

  React.useEffect(() => {
    if (!active) {
      setEntered(false);
      return;
    }

    setEntered(false);
    const frame = window.requestAnimationFrame(() => {
      if (!mountedRef.current) return;
      setEntered(true);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [active, mountedRef]);

  return entered;
}

export default function AudioWitnessBar({
  visible,
  focusScope,
  title,
  subtitle,
  mediaType,
  activeMediaUrl,
  activeSourceId,
  activeSourceLabel,
  activeClipId,
  activeClipLabel,
  activeSequenceLabel,
  status,
  currentTimeSec,
  durationSec,
  auditionMode,
  bindingMode,
  mediaFamilyLabel,
  hasLinkedAudio = false,
  hasLinkedVideo = false,
  hasExplicitBinding = false,
  canBindCurrentFamily = false,
  familyOptions = [],
  selectedFamilyId,
  audioMembers = [],
  videoMembers = [],
  selectedAudioMemberId,
  selectedVideoMemberId,
  onSetAuditionMode,
  onBindCurrentFamily,
  onClearExplicitBinding,
  onSelectFamily,
  onSelectAudioMember,
  onSelectVideoMember,
  onInspectMediaMember,
  emotionalSummary,
  activeLensId = null,
  pinnedMiniLensId = null,
  availableLensIds,
  onToggleLens,
  onPinMiniLens,
  onRemoveActiveClip,
  onClose,
}: AudioWitnessBarProps): JSX.Element | null {
  const { state: timelineState, addAnnotation, updateAnnotation, narrativeSonicsReadModel } = useOmniCineTimeline();
  const { narrativeSummary, groups, proposals: compilerProposals } = useDirectorCompilerReadOnly();
  const contextLabel = subtitle || `${focusScopeLabel(focusScope)} Context`;
  const mediaFocusLabel = `${mediaType.charAt(0).toUpperCase()}${mediaType.slice(1)} Focus`;
  const prefersLightMode = usePrefersLightMode();
  const entered = usePresenceMotion(visible);
  const [bindingPanelOpen, setBindingPanelOpen] = React.useState(false);
  const bindingPopoverRef = React.useRef<HTMLDivElement | null>(null);
  const bindingToggleRef = React.useRef<HTMLButtonElement | null>(null);
  const [videoActionPanelOpen, setVideoActionPanelOpen] = React.useState<VideoActionPanelId | null>(null);
  const [actionBusyId, setActionBusyId] = React.useState<string | null>(null);
  const [annotationUiState, dispatchAnnotationUi] = React.useReducer(annotationUiReducer, undefined, createInitialAnnotationUiState);
  const annotationUiStateRef = React.useRef(annotationUiState);
  React.useEffect(() => {
    annotationUiStateRef.current = annotationUiState;
  }, [annotationUiState]);
  const annotationRecordsRef = React.useRef(timelineState.annotations ?? []);
  React.useEffect(() => {
    annotationRecordsRef.current = timelineState.annotations ?? [];
  }, [timelineState.annotations]);
  const {
    draft: annotationDraft,
    actionDraft: annotationActionDraft,
    kind: annotationKind,
    editingAnnotationId,
    handoffContext: annotationHandoffContext,
    sourceOverride: annotationSourceOverride,
    aiSuggestions: annotationAiSuggestions,
    aiStatus: annotationAiStatus,
    editingSuggestionId,
    suggestionContext: annotationSuggestionContext,
    voiceActive: annotationVoiceActive,
    voiceInterim: annotationVoiceInterim,
    voiceFinal: annotationVoiceFinal,
  } = annotationUiState;
  const patchAnnotationUi = React.useCallback((patch: Partial<AnnotationUiState>) => {
    dispatchAnnotationUi({ type: "patch", patch });
  }, []);
  const setAnnotationDraft = React.useCallback((value: React.SetStateAction<string>) => {
    patchAnnotationUi({ draft: resolveStateValue(annotationUiStateRef.current.draft, value) });
  }, [patchAnnotationUi]);
  const setAnnotationActionDraft = React.useCallback((value: React.SetStateAction<string>) => {
    patchAnnotationUi({ actionDraft: resolveStateValue(annotationUiStateRef.current.actionDraft, value) });
  }, [patchAnnotationUi]);
  const setAnnotationKind = React.useCallback((value: React.SetStateAction<AnnotationSurfaceKind>) => {
    patchAnnotationUi({ kind: resolveStateValue(annotationUiStateRef.current.kind, value) });
  }, [patchAnnotationUi]);
  const setAnnotationSourceOverride = React.useCallback((value: React.SetStateAction<AnnotationCaptureSource | null>) => {
    patchAnnotationUi({ sourceOverride: resolveStateValue(annotationUiStateRef.current.sourceOverride, value) });
  }, [patchAnnotationUi]);
  const setAnnotationAiSuggestions = React.useCallback((value: React.SetStateAction<AnnotationSuggestion[]>) => {
    patchAnnotationUi({ aiSuggestions: resolveStateValue(annotationUiStateRef.current.aiSuggestions, value) });
  }, [patchAnnotationUi]);
  const setAnnotationAiStatus = React.useCallback((value: React.SetStateAction<string | null>) => {
    patchAnnotationUi({ aiStatus: resolveStateValue(annotationUiStateRef.current.aiStatus, value) });
  }, [patchAnnotationUi]);
  const setEditingSuggestionId = React.useCallback((value: React.SetStateAction<string | null>) => {
    patchAnnotationUi({ editingSuggestionId: resolveStateValue(annotationUiStateRef.current.editingSuggestionId, value) });
  }, [patchAnnotationUi]);
  const setAnnotationSuggestionContext = React.useCallback((value: React.SetStateAction<AnnotationSuggestionContext | null>) => {
    patchAnnotationUi({ suggestionContext: resolveStateValue(annotationUiStateRef.current.suggestionContext, value) });
  }, [patchAnnotationUi]);
  const setAnnotationVoiceActive = React.useCallback((value: React.SetStateAction<boolean>) => {
    patchAnnotationUi({ voiceActive: resolveStateValue(annotationUiStateRef.current.voiceActive, value) });
  }, [patchAnnotationUi]);
  const setAnnotationVoiceInterim = React.useCallback((value: React.SetStateAction<string>) => {
    patchAnnotationUi({ voiceInterim: resolveStateValue(annotationUiStateRef.current.voiceInterim, value) });
  }, [patchAnnotationUi]);
  const setAnnotationVoiceFinal = React.useCallback((value: React.SetStateAction<string | null>) => {
    patchAnnotationUi({ voiceFinal: resolveStateValue(annotationUiStateRef.current.voiceFinal, value) });
  }, [patchAnnotationUi]);
  const [publishDraft, setPublishDraft] = React.useState("");
  const videoActionPopoverRef = React.useRef<HTMLDivElement | null>(null);
  const videoActionToggleRowRef = React.useRef<HTMLDivElement | null>(null);
  const [videoActionPanelPresence, setVideoActionPanelPresence] = React.useState<VideoActionPanelId | null>(null);
  const [videoActionPopoverOffsetY, setVideoActionPopoverOffsetY] = React.useState(0);
  const videoActionPanelEntered = usePresenceMotion(!!videoActionPanelOpen);
  const mountedRef = useMountedRef();
  const previousAnnotationPanelStateRef = React.useRef<{ open: VideoActionPanelId | null; presence: VideoActionPanelId | null }>({
    open: null,
    presence: null,
  });

  const traceAnnotationSurface = React.useCallback(
    (action: string, detail: Record<string, string | number | boolean | null | undefined> = {}) => {
      const currentAnnotationUiState = annotationUiStateRef.current;
      emitAnnotationSurfaceTrace(action, {
        visible,
        openPanel: videoActionPanelOpen,
        presencePanel: videoActionPanelPresence,
        kind: currentAnnotationUiState.kind,
        draftLength: currentAnnotationUiState.draft.trim().length,
        nextActionLength: currentAnnotationUiState.actionDraft.trim().length,
        suggestionCount: currentAnnotationUiState.aiSuggestions.length,
        editingSuggestionActive: !!currentAnnotationUiState.editingSuggestionId,
        voiceActive: currentAnnotationUiState.voiceActive,
        ...detail,
      });
    },
    [videoActionPanelOpen, videoActionPanelPresence, visible],
  );

  const applyAnnotationDraftHandoff = React.useCallback((detail: OmniCineAnnotationDraftHandoffDetail, reason: string) => {
    const draftNote = String(detail.draftNote ?? "").trim();
    if (!draftNote) return false;

    patchAnnotationUi({
      kind: normalizeAnnotationHandoffKind(detail.suggestedKind),
      draft: draftNote,
      actionDraft: String(detail.draftNextAction ?? "").trim(),
      editingAnnotationId: null,
      handoffContext: {
        sourceSurface: "omnistudio",
        sourceLabel: detail.sourceLabel ?? null,
        sourceNoteId: detail.sourceNoteId ?? null,
        sourceSceneId: detail.sourceSceneId ?? null,
        sourceSceneTitle: detail.sourceSceneTitle ?? null,
      },
      editingSuggestionId: null,
      sourceOverride: "manual",
      suggestionContext: null,
      aiSuggestions: [],
      aiStatus: detail.sourceLabel
        ? `Loaded OmniStudio draft from ${detail.sourceLabel}.`
        : "Loaded OmniStudio draft.",
      voiceActive: false,
      voiceInterim: "",
      voiceFinal: null,
    });
    traceAnnotationSurface("open_request", {
      reason,
      sourceSurface: detail.sourceSurface,
      sourceNoteId: detail.sourceNoteId ?? null,
      sourceSceneId: detail.sourceSceneId ?? null,
      hasNextAction: !!detail.draftNextAction,
    });
    setBindingPanelOpen(false);
    if (detail.openSurface !== false) {
      setVideoActionPanelOpen("annotate");
    }
    return true;
  }, [patchAnnotationUi, traceAnnotationSurface]);

  React.useEffect(() => {
    if (!visible) setBindingPanelOpen(false);
    if (!visible) {
      if (videoActionPanelOpen === "annotate" || videoActionPanelPresence === "annotate" || annotationUiStateRef.current.draft.trim() || annotationUiStateRef.current.actionDraft.trim()) {
        traceAnnotationSurface("host_hidden", {
          reason: "witness_bar_hidden",
        });
      }
      setVideoActionPanelOpen(null);
      setVideoActionPanelPresence(null);
      setActionBusyId(null);
      dispatchAnnotationUi({ type: "reset" });
      setPublishDraft("");
    }
  }, [traceAnnotationSurface, videoActionPanelOpen, videoActionPanelPresence, visible]);

  React.useEffect(() => {
    const previousState = previousAnnotationPanelStateRef.current;
    if (previousState.open === videoActionPanelOpen && previousState.presence === videoActionPanelPresence) return;

    const annotateOpen = videoActionPanelOpen === "annotate";
    const annotatePresent = videoActionPanelPresence === "annotate";
    const transitionPhase = classifyAnnotationPanelPhase(previousState, {
      open: videoActionPanelOpen,
      presence: videoActionPanelPresence,
    });
    traceAnnotationSurface("state_transition", {
      previousOpenPanel: previousState.open,
      nextOpenPanel: videoActionPanelOpen,
      previousPresencePanel: previousState.presence,
      nextPresencePanel: videoActionPanelPresence,
      annotateOpen,
      annotatePresent,
      transitionPhase,
    });

    if (transitionPhase === "unexpected_divergence") {
      emitVideoOpsMetric("omnicine.video.annotate.panel.divergence", "warning", 1, OMNICINE_VIDEO_ANNOTATE_EVENT);
      traceAnnotationSurface("state_divergence", {
        previousOpenPanel: previousState.open,
        previousPresencePanel: previousState.presence,
        nextOpenPanel: videoActionPanelOpen,
        nextPresencePanel: videoActionPanelPresence,
        annotateOpen,
        annotatePresent,
        transitionPhase,
      });
    }

    previousAnnotationPanelStateRef.current = {
      open: videoActionPanelOpen,
      presence: videoActionPanelPresence,
    };
  }, [traceAnnotationSurface, videoActionPanelOpen, videoActionPanelPresence]);

  React.useEffect(() => {
    if (videoActionPanelOpen) {
      setVideoActionPanelPresence(videoActionPanelOpen);
      return;
    }

    if (!videoActionPanelPresence) return;
    const timeout = window.setTimeout(() => {
      if (!mountedRef.current) return;
      setVideoActionPanelPresence(null);
    }, 220);
    return () => window.clearTimeout(timeout);
  }, [mountedRef, videoActionPanelOpen, videoActionPanelPresence]);

  React.useLayoutEffect(() => {
    if (!videoActionPanelPresence) {
      setVideoActionPopoverOffsetY(0);
      return;
    }

    const updatePopoverOffset = () => {
      if (!mountedRef.current) return;
      const node = videoActionPopoverRef.current;
      if (!node) {
        setVideoActionPopoverOffsetY(0);
        return;
      }

      const rect = node.getBoundingClientRect();
      const viewportPadding = 16;
      const nextOffsetY = rect.top < viewportPadding ? viewportPadding - rect.top : 0;
      setVideoActionPopoverOffsetY((currentOffset) => (Math.abs(currentOffset - nextOffsetY) < 1 ? currentOffset : nextOffsetY));
    };

    const frame = window.requestAnimationFrame(updatePopoverOffset);
    window.addEventListener("resize", updatePopoverOffset);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", updatePopoverOffset);
    };
  }, [mountedRef, videoActionPanelEntered, videoActionPanelPresence]);

  React.useEffect(() => {
    if (!bindingPanelOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (bindingPopoverRef.current?.contains(target)) return;
      if (bindingToggleRef.current?.contains(target)) return;
      setBindingPanelOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [bindingPanelOpen]);

  React.useEffect(() => {
    if (!videoActionPanelOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (videoActionPopoverRef.current?.contains(target)) return;
      if (videoActionToggleRowRef.current?.contains(target)) return;
      if (videoActionPanelOpen === "annotate") {
        traceAnnotationSurface("close_request", {
          reason: "outside_pointer",
        });
      }
      setVideoActionPanelOpen(null);
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [traceAnnotationSurface, videoActionPanelOpen]);

  React.useEffect(() => {
    const onOpenSurface: EventListener = (event) => {
      const detail = ((event as CustomEvent).detail ?? {}) as { panel?: VideoActionPanelId; annotationId?: string };
      if (!visible) return;
      if ((detail.panel ?? "annotate") !== "annotate") return;
      const annotationId = String(detail.annotationId ?? "").trim();
      traceAnnotationSurface("open_request", {
        reason: "window_event",
        hasAnnotationId: !!annotationId,
      });
      if (annotationId) {
        const annotation = (timelineState.annotations ?? []).find((entry) => entry.id === annotationId) ?? null;
        if (annotation) {
          patchAnnotationUi({
            kind: annotation.type,
            draft: annotation.summary ?? annotation.rawNote,
            actionDraft: annotation.nextAction ?? annotation.action ?? "",
            editingAnnotationId: annotation.id,
            handoffContext: null,
            editingSuggestionId: null,
            sourceOverride: annotation.source,
            suggestionContext: annotation.provenance?.suggestionId
              ? {
                  suggestionId: annotation.provenance.suggestionId,
                  suggestionSource: annotation.provenance.suggestionSource ?? "panel",
                  suggestionDisposition: normalizeSuggestionDisposition(annotation.provenance.suggestionDisposition),
                  generatedAt: annotation.provenance.generatedAt ?? null,
                  originalSuggestionText: annotation.provenance.originalSuggestionText ?? annotation.rawNote,
                }
              : null,
            voiceActive: false,
            voiceInterim: "",
            voiceFinal: null,
            aiStatus: "Editing existing annotation.",
          });
        }
      }
      setBindingPanelOpen(false);
      setVideoActionPanelOpen("annotate");
    };

    window.addEventListener(OMNICINE_ANNOTATION_SURFACE_OPEN_EVENT, onOpenSurface);
    return () => window.removeEventListener(OMNICINE_ANNOTATION_SURFACE_OPEN_EVENT, onOpenSurface);
  }, [patchAnnotationUi, timelineState.annotations, traceAnnotationSurface, visible]);

  React.useEffect(() => {
    if (!visible) return;

    const pending = consumePendingOmniCineAnnotationDraftHandoff();
    if (!pending) return;

    applyAnnotationDraftHandoff(pending, "omnistudio_pending_handoff");
  }, [applyAnnotationDraftHandoff, visible]);

  React.useEffect(() => {
    const unsubscribe = onOmniCineAnnotationDraftHandoff((detail) => {
      if (!visible) return;

      const applied = applyAnnotationDraftHandoff(detail, "omnistudio_event_handoff");
      if (applied) {
        consumePendingOmniCineAnnotationDraftHandoff();
      }
    });

    return unsubscribe;
  }, [applyAnnotationDraftHandoff, visible]);

  React.useEffect(() => {
    const onAiSuggestionsQueued: EventListener = (event) => {
      const detail = ((event as CustomEvent).detail ?? {}) as {
        suggestions?: AnnotationSuggestion[];
        openSurface?: boolean;
        replaceExisting?: boolean;
        source?: AnnotationSuggestion["suggestionSource"];
        queuedAt?: number;
      };

      const suggestions = Array.isArray(detail.suggestions)
        ? detail.suggestions.map((suggestion) => ({
            ...suggestion,
            queuedAt: detail.queuedAt ?? Date.now(),
            suggestionSource: detail.source ?? "inspector",
          }))
        : [];
      if (!suggestions.length) return;

      const nextSuggestions = mergeSuggestionQueue(annotationUiStateRef.current.aiSuggestions, suggestions, !!detail.replaceExisting);
      patchAnnotationUi({
        aiSuggestions: nextSuggestions,
        aiStatus: `Queued ${suggestions.length} AI suggestion${suggestions.length === 1 ? "" : "s"} for review.`,
      });

      if (detail.openSurface !== false && visible) {
        traceAnnotationSurface("open_request", {
          reason: "ai_suggestions_queued",
          queuedSuggestions: suggestions.length,
          replaceExisting: !!detail.replaceExisting,
        });
        setBindingPanelOpen(false);
        setVideoActionPanelOpen("annotate");
      }
    };

    window.addEventListener(OMNICINE_AI_ANNOTATION_SUGGESTIONS_EVENT, onAiSuggestionsQueued);
    return () => window.removeEventListener(OMNICINE_AI_ANNOTATION_SUGGESTIONS_EVENT, onAiSuggestionsQueued);
  }, [patchAnnotationUi, visible]);

  const activeMode = auditionMode ?? focusScope;
  const compactLabel = compactMediaLabel(activeClipLabel || activeSourceLabel || title);
  const modeSummary = modeTickerText(activeMode, compactLabel, activeSequenceLabel);
  const bindingSummary = bindingTickerText(bindingMode, hasLinkedAudio, hasLinkedVideo);
  const isVideoFocus = mediaType === "video";
  const isArcLensPrimary = activeLensId === "arc";
  const arcAuditionSummary = compactArcAuditionSummary(auditionMode, mediaFocusLabel);
  const arcModeSummary = compactArcModeSummary(activeMode);
  const arcBindingSummary = compactArcBindingSummary(bindingMode, hasLinkedAudio, hasLinkedVideo);
  const videoShareSummary = [title, mediaFamilyLabel, activeClipId].filter(Boolean).join(" • ");
  const frameSummary = `${copyableTimecode(currentTimeSec)} / ${copyableTimecode(durationSec)}`;
  const frameFileToken = copyableTimecode(currentTimeSec).replace(/:/g, "-");
  const clipDownloadName = `${slugifyLabel(title)}.${inferExtensionFromUrl(activeMediaUrl)}`;
  const exportFileName = `${slugifyLabel(title)}.podcastExport.v1.json`;
  const publishSummary = publishDraft.trim() || `Ready to publish ${compactLabel}`;
  const annotationFps = 30;
  const annotationTimecode = formatAnnotationTimecode(currentTimeSec, annotationFps);
  const annotationFrame = annotationFrameIndex(currentTimeSec, annotationFps);
  const annotationAssetLabel = activeClipLabel || title || activeSourceLabel || activeSourceId || "active clip";
  const annotationRecords = timelineState.annotations ?? [];
  const activeAnnotationRecords = React.useMemo(
    () =>
      annotationRecords.filter((record) => {
        if (activeClipId && record.clipId) return record.clipId === activeClipId;
        return record.assetLabel === annotationAssetLabel;
      }),
    [activeClipId, annotationAssetLabel, annotationRecords],
  );
  const editingAnnotationRecord = React.useMemo(
    () => (editingAnnotationId ? annotationRecords.find((record) => record.id === editingAnnotationId) ?? null : null),
    [annotationRecords, editingAnnotationId],
  );
  const annotationDraftState = React.useMemo<AnnotationSurfaceDraft>(
    () => ({
      draftNote: annotationDraft,
      draftKind: annotationKind,
      draftNextAction: annotationActionDraft,
      selectedSuggestionId: annotationSuggestionContext?.suggestionId ?? editingSuggestionId,
      markerVisible: true,
      isDirty: [annotationDraft, annotationActionDraft].some((value) => value.trim().length > 0) || !!annotationSuggestionContext,
      validation: {},
    }),
    [annotationActionDraft, annotationDraft, annotationKind, annotationSuggestionContext, editingSuggestionId],
  );
  const annotationValidationState = React.useMemo<AnnotationSurfaceValidationState>(
    () => {
      const errors: AnnotationSurfaceValidationState["errors"] = {};
      if (!annotationDraft.trim()) {
        errors.draftNote = "Add an annotation note before publishing.";
      }
      if (!annotationKind) {
        errors.selectedKind = "Choose an annotation type before publishing.";
      }
      if ((annotationKind === "intent" || annotationKind === "publish") && !annotationActionDraft.trim()) {
        errors.draftNextAction = "Add the next action before publishing this annotation.";
      }

      return {
        canPublish: Object.keys(errors).length === 0,
        errors,
      };
    },
    [annotationActionDraft, annotationDraft, annotationKind],
  );
  const annotationSuggestionViewState = React.useMemo<AnnotationSuggestionViewState>(
    () => ({
      selectedSuggestionId: annotationSuggestionContext?.suggestionId ?? null,
      editingSuggestionId,
    }),
    [annotationSuggestionContext, editingSuggestionId],
  );
  const showAnnotationHeaderContext = videoActionPanelOpen === "annotate" || videoActionPanelPresence === "annotate";
  const activeFocusEntity =
    activeMode === "source"
      ? activeSourceLabel || title || activeSourceId || "No active source"
      : activeMode === "sequence"
        ? activeSequenceLabel || "Timeline sequence"
        : activeClipLabel || title || activeClipId || activeSourceLabel || "No active clip";

  const emitVideoActionEvent = React.useCallback(
    (type: string, extraDetail: Record<string, unknown> = {}) => {
      emitWindowEvent(type, {
        mediaType,
        focusScope: activeMode,
        title,
        clipId: activeClipId ?? null,
        clipLabel: activeClipLabel ?? title,
        sourceId: activeSourceId ?? null,
        sourceLabel: activeSourceLabel ?? null,
        sequenceLabel: activeSequenceLabel ?? null,
        mediaUrl: activeMediaUrl ?? null,
        mediaFamilyLabel: mediaFamilyLabel ?? null,
        status,
        currentTimeSec: currentTimeSec ?? null,
        durationSec: durationSec ?? null,
        ...extraDetail,
      });
    },
    [
      activeClipId,
      activeClipLabel,
      activeMediaUrl,
      activeMode,
      activeSequenceLabel,
      activeSourceId,
      activeSourceLabel,
      currentTimeSec,
      durationSec,
      mediaFamilyLabel,
      mediaType,
      status,
      title,
    ],
  );

  const handleNativeShare = React.useCallback(async () => {
    const shareText = activeMediaUrl ?? videoShareSummary ?? title;
    const sharePayload = {
      title,
      text: videoShareSummary || title,
      url: activeMediaUrl ?? undefined,
    };

    setActionBusyId("share-native");
  emitVideoOpsMetric("omnicine.video.share.requested", "normal", 1, OMNICINE_VIDEO_SHARE_EVENT);
    emitVideoActionEvent(OMNICINE_VIDEO_SHARE_EVENT, { stage: "request", transport: "native", sharePayload });

    try {
      const shareApi = navigator as Navigator & {
        share?: (data: ShareData) => Promise<void>;
        canShare?: (data: ShareData) => boolean;
      };

      if (typeof shareApi.share === "function" && (!shareApi.canShare || shareApi.canShare(sharePayload))) {
        await shareApi.share(sharePayload);
        emitVideoOpsMetric("omnicine.video.share.native.success", "normal", 1, OMNICINE_VIDEO_SHARE_RESULT_EVENT);
        emitVideoActionEvent(OMNICINE_VIDEO_SHARE_RESULT_EVENT, { status: "ok", transport: "native" });
        emitOmniCineToast("success", "Share sent", "Native share sheet opened for the focused clip.");
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareText);
        emitVideoOpsMetric("omnicine.video.share.clipboard.success", "normal", 1, OMNICINE_VIDEO_SHARE_RESULT_EVENT);
        emitVideoActionEvent(OMNICINE_VIDEO_SHARE_RESULT_EVENT, {
          status: "ok",
          transport: "clipboard",
          copiedText: shareText,
        });
        emitOmniCineToast("success", "Share copied", "Share details were copied because system share is unavailable.");
      } else {
        throw new Error("Share is unavailable in this browser.");
      }
    } catch (error) {
      emitVideoOpsMetric("omnicine.video.share.failed", "warning", 1, OMNICINE_VIDEO_SHARE_RESULT_EVENT);
      emitVideoActionEvent(OMNICINE_VIDEO_SHARE_RESULT_EVENT, {
        status: "error",
        transport: "native",
        message: error instanceof Error ? error.message : String(error),
      });
      emitOmniCineToast("warning", "Share unavailable", "The focused clip could not be shared from this browser surface.");
    } finally {
      setActionBusyId(null);
    }
  }, [activeMediaUrl, emitVideoActionEvent, title, videoShareSummary]);

  const handleOpenClip = React.useCallback(() => {
    if (!activeMediaUrl) {
      emitOmniCineToast("warning", "No clip URL", "The focused clip does not expose a direct media URL yet.");
      return;
    }

    window.open(activeMediaUrl, "_blank", "noopener,noreferrer");
    emitVideoOpsMetric("omnicine.video.share.opened", "normal", 1, OMNICINE_VIDEO_SHARE_EVENT);
    emitVideoActionEvent(OMNICINE_VIDEO_SHARE_EVENT, { stage: "open", transport: "window" });
    emitOmniCineToast("info", "Clip opened", "Opened the focused video in a separate browser tab.");
  }, [activeMediaUrl, emitVideoActionEvent]);

  const handleCaptureFrame = React.useCallback(async () => {
    setActionBusyId("frame-capture");
    emitVideoActionEvent(OMNICINE_VIDEO_FRAME_CAPTURE_EVENT, { stage: "request" });

    try {
      const video = findActiveVideoElement();
      if (!video) throw new Error("No active video surface is mounted.");

      const blob = await captureVideoFrameBlob(video);
      const filename = `${slugifyLabel(title)}-frame-${frameFileToken}.png`;
      downloadBlob(blob, filename);
      emitVideoActionEvent(OMNICINE_VIDEO_FRAME_CAPTURE_RESULT_EVENT, {
        status: "ok",
        filename,
        width: video.videoWidth,
        height: video.videoHeight,
      });
      emitOmniCineToast("success", "Frame captured", `Saved ${filename} from the active video surface.`);
    } catch (error) {
      emitVideoActionEvent(OMNICINE_VIDEO_FRAME_CAPTURE_RESULT_EVENT, {
        status: "error",
        message: error instanceof Error ? error.message : String(error),
      });
      emitOmniCineToast("warning", "Frame capture failed", "The current frame could not be captured from the focused video.");
    } finally {
      setActionBusyId(null);
    }
  }, [emitVideoActionEvent, frameFileToken, title]);

  const handleAddFrameMarker = React.useCallback(() => {
    const label = `Frame ${copyableTimecode(currentTimeSec)}`;
    emitVideoAnnotateAddMarker({
      mediaType,
      focusScope: activeMode,
      title,
      clipId: activeClipId ?? null,
      clipLabel: activeClipLabel ?? title,
      sourceId: activeSourceId ?? null,
      sourceLabel: activeSourceLabel ?? null,
      sequenceLabel: activeSequenceLabel ?? null,
      mediaUrl: activeMediaUrl ?? null,
      mediaFamilyLabel: mediaFamilyLabel ?? null,
      status,
      currentTimeSec: currentTimeSec ?? null,
      durationSec: durationSec ?? null,
      label,
      intent: "frame",
    });
    emitOmniCineToast("success", "Frame marker added", `Dropped ${label} on the timeline.`);
  }, [activeClipId, activeClipLabel, activeMediaUrl, activeMode, activeSequenceLabel, activeSourceId, activeSourceLabel, currentTimeSec, durationSec, mediaFamilyLabel, mediaType, status, title]);

  const handleDownloadClip = React.useCallback((surface: "share" | "export" = "share") => {
    if (!activeMediaUrl) {
      emitOmniCineToast("warning", "No clip URL", "The focused clip does not expose a downloadable media URL yet.");
      return;
    }

    downloadUrl(activeMediaUrl, clipDownloadName);
    emitVideoOpsMetric(
      surface === "export" ? "omnicine.video.download.direct.started" : "omnicine.video.download.started",
      "normal",
      1,
      OMNICINE_VIDEO_SHARE_EVENT,
    );
    emitVideoActionEvent(OMNICINE_VIDEO_SHARE_EVENT, { stage: surface === "export" ? "direct-download" : "download", filename: clipDownloadName });
    emitOmniCineToast("info", "Download started", `Started a direct download for ${clipDownloadName}.`);
  }, [activeMediaUrl, clipDownloadName, emitVideoActionEvent]);

  const handleExportPackage = React.useCallback(async () => {
    const requestId = makeRequestId();
    setActionBusyId("export-package");
    emitVideoActionEvent(OMNICINE_VIDEO_EXPORT_EVENT, {
      stage: "request",
      requestId,
      kind: "podcast.v1",
      format: "json",
      filename: exportFileName,
    });

    try {
      const result = await new Promise<Record<string, unknown>>((resolve, reject) => {
        let onResult: EventListener;
        const timeout = window.setTimeout(() => {
          window.removeEventListener(OMNICINE_EXPORT_RESULT_EVENT, onResult);
          reject(new Error("Timed out waiting for export data."));
        }, 5000);

        onResult = (event: Event) => {
          const detail = (((event as CustomEvent).detail ?? {}) as Record<string, unknown>) || {};
          if (detail.requestId !== requestId) return;

          window.clearTimeout(timeout);
          window.removeEventListener(OMNICINE_EXPORT_RESULT_EVENT, onResult);
          resolve(detail);
        };

        window.addEventListener(OMNICINE_EXPORT_RESULT_EVENT, onResult);
        emitWindowEvent(OMNICINE_EXPORT_EVENT, {
          requestId,
          kind: "podcast.v1",
          format: "json",
          filename: exportFileName,
          source: "transport",
          ui: { autoDownload: false, source: "AudioWitnessBar" },
          meta: {
            title,
            description: `Focused video export for ${title}`,
          },
        });
      });

      const rootBlob = result.blob instanceof Blob ? result.blob : undefined;
      const rootFilename = typeof result.filename === "string" ? result.filename : undefined;
      const rootError = typeof result.error === "string" ? result.error : undefined;
      const podcast = result.podcast as { blob?: Blob; filename?: string; error?: { message?: string } } | undefined;
      const exportBlob = rootBlob ?? podcast?.blob;
      const resolvedFilename = rootFilename ?? podcast?.filename ?? exportFileName;
      const exportError = rootError ?? podcast?.error?.message;
      const isOk = result.status === "ok" || result.ok === true;

      if (!isOk || !exportBlob) {
        throw new Error(exportError || "Export bridge returned no file.");
      }

      downloadBlob(exportBlob, resolvedFilename);
      emitVideoActionEvent(OMNICINE_VIDEO_EXPORT_RESULT_EVENT, {
        status: "ok",
        requestId,
        format: result.format ?? "json",
        filename: resolvedFilename,
      });
      emitOmniCineToast("success", "Export ready", `Downloaded ${resolvedFilename}.`);
    } catch (error) {
      emitVideoActionEvent(OMNICINE_VIDEO_EXPORT_RESULT_EVENT, {
        status: "error",
        requestId,
        message: error instanceof Error ? error.message : String(error),
      });
      emitOmniCineToast("error", "Export failed", error instanceof Error ? error.message : String(error));
    } finally {
      setActionBusyId(null);
    }
  }, [emitVideoActionEvent, exportFileName, title]);

  const clearAnnotationDraftLifecycle = React.useCallback((options?: { removeSuggestionId?: string | null; closePanel?: boolean; reason?: string }) => {
    const removeSuggestionId = String(options?.removeSuggestionId ?? "").trim();
    const nextSuggestions = removeSuggestionId
      ? annotationUiStateRef.current.aiSuggestions.filter((suggestion) => suggestion.id !== removeSuggestionId)
      : annotationUiStateRef.current.aiSuggestions;

    traceAnnotationSurface("draft_reset", {
      reason: options?.reason ?? "unspecified",
      closePanel: !!options?.closePanel,
      removedSuggestion: !!removeSuggestionId,
      remainingSuggestions: nextSuggestions.length,
    });

    if (options?.closePanel && videoActionPanelOpen === "annotate") {
      traceAnnotationSurface("close_request", {
        reason: options.reason ?? "draft_reset",
      });
    }

    patchAnnotationUi({
      draft: "",
      actionDraft: "",
      editingAnnotationId: null,
      handoffContext: null,
      sourceOverride: null,
      aiStatus: null,
      suggestionContext: null,
      voiceActive: false,
      voiceInterim: "",
      voiceFinal: null,
      editingSuggestionId: null,
      aiSuggestions: nextSuggestions,
    });

    if (options?.closePanel) {
      setVideoActionPanelOpen(null);
    }
  }, [patchAnnotationUi, traceAnnotationSurface, videoActionPanelOpen]);

  const emitAnnotationMarkerProjection = React.useCallback((annotation: AnnotationRecord, annotationState: "published" | "marker_only") => {
    emitVideoAnnotateAddMarker({
      mediaType,
      focusScope: activeMode,
      title,
      clipId: annotation.clipId,
      clipLabel: activeClipLabel ?? title,
      sourceId: activeSourceId ?? null,
      sourceLabel: activeSourceLabel ?? null,
      sequenceLabel: activeSequenceLabel ?? null,
      mediaUrl: activeMediaUrl ?? null,
      mediaFamilyLabel: mediaFamilyLabel ?? null,
      status,
      currentTimeSec: currentTimeSec ?? null,
      durationSec: durationSec ?? null,
      timeSec: annotation.timeSec,
      label: annotation.rawNote,
      intent: "annotation",
      annotationId: annotation.id,
      annotationType: annotation.type,
      annotationSummary: annotation.summary ?? annotation.rawNote,
      annotationSource: annotation.source,
      nextAction: annotation.nextAction ?? annotation.action ?? null,
      markerVisible: annotation.markerVisible !== false,
      annotationProvenance: annotation.provenance,
      assetLabel: annotation.assetLabel,
      linkedAudio: annotation.linkedAudio,
      frameIndex: annotation.frameIndex,
      anchorTimecode: annotation.anchorTimecode,
      createdAt: annotation.createdAt,
      annotationState,
    });
  }, [activeClipLabel, activeMediaUrl, activeMode, activeSequenceLabel, activeSourceId, activeSourceLabel, currentTimeSec, durationSec, mediaFamilyLabel, mediaType, status, title]);

  const editAnnotationInSurface = React.useCallback((annotation: AnnotationRecord, reason: string) => {
    patchAnnotationUi({
      kind: annotation.type,
      draft: annotation.summary ?? annotation.rawNote,
      actionDraft: annotation.nextAction ?? annotation.action ?? "",
      editingAnnotationId: annotation.id,
      editingSuggestionId: null,
      sourceOverride: annotation.source,
      suggestionContext: annotation.provenance?.suggestionId
        ? {
            suggestionId: annotation.provenance.suggestionId,
            suggestionSource: annotation.provenance.suggestionSource ?? "panel",
                suggestionDisposition: normalizeSuggestionDisposition(annotation.provenance.suggestionDisposition),
            generatedAt: annotation.provenance.generatedAt ?? null,
            originalSuggestionText: annotation.provenance.originalSuggestionText ?? annotation.rawNote,
          }
        : null,
      voiceActive: false,
      voiceInterim: "",
      voiceFinal: null,
      aiStatus: "Editing existing annotation.",
    });
    traceAnnotationSurface("open_request", {
      reason,
      editingAnnotationId: annotation.id,
    });
    setBindingPanelOpen(false);
    setVideoActionPanelOpen("annotate");
  }, [patchAnnotationUi, traceAnnotationSurface]);

  const handleEmitAnnotation = React.useCallback((overrides?: { note?: string; kind?: AnnotationSurfaceKind; source?: AnnotationCaptureSource; action?: string; closeAfterPublish?: boolean }) => {
    const currentEditingAnnotationId = annotationUiStateRef.current.editingAnnotationId;
    const currentEditingAnnotationRecord = currentEditingAnnotationId
      ? annotationRecordsRef.current.find((record) => record.id === currentEditingAnnotationId) ?? null
      : null;
    const resolvedKind = overrides?.kind ?? annotationKind;
    const source: AnnotationCaptureSource = overrides?.source ?? annotationSourceOverride ?? (annotationVoiceActive ? "voice" : "manual");
    const note = overrides?.note?.trim() || annotationDraft.trim() || `${annotationKindLabel(resolvedKind)} note @ ${annotationTimecode}`;
    const nextAction = overrides?.action?.trim() || annotationActionDraft.trim() || null;

    const canonicalAnnotation = resolveCanonicalAnnotationRecord({
      existingAnnotation: currentEditingAnnotationRecord,
      kind: resolvedKind,
      captureSource: source,
      assetLabel: annotationAssetLabel,
      clipId: activeClipId ?? null,
      linkedAudio: hasLinkedAudio,
      frameIndex: annotationFrame,
      anchorTimecode: annotationTimecode,
      timeSec: currentTimeSec ?? null,
      rawNote: note,
      nextAction,
      markerVisible: true,
      suggestionContext: annotationSuggestionContext,
    });

    if (currentEditingAnnotationRecord) {
      updateAnnotation(
        currentEditingAnnotationRecord.id,
        buildAnnotationUpdatePatch(canonicalAnnotation),
        { source: "user", note: "annotation surface update" },
      );
    } else {
      addAnnotation(canonicalAnnotation, { source: "user", note: "annotation surface publish" });
    }
    emitAnnotationMarkerProjection(canonicalAnnotation, "published");
    traceAnnotationSurface("publish_commit", {
      reason: overrides?.closeAfterPublish ? "publish_and_close" : "publish",
      annotationType: resolvedKind,
      annotationSource: source,
      closeAfterPublish: !!overrides?.closeAfterPublish,
      hasNextAction: !!nextAction,
      editingAnnotation: !!currentEditingAnnotationRecord,
    });

    emitVideoActionEvent(OMNICINE_VIDEO_ANNOTATE_EVENT, {
      note,
      intent: "annotation",
      annotationType: resolvedKind,
      annotationSource: source,
      assetLabel: annotationAssetLabel,
      linkedAudio: hasLinkedAudio,
      frameIndex: annotationFrame,
      anchorTimecode: annotationTimecode,
      annotation: {
        id: canonicalAnnotation.id,
        type: canonicalAnnotation.type,
        asset: canonicalAnnotation.assetLabel,
        clipId: canonicalAnnotation.clipId,
        anchor: {
          timeSec: canonicalAnnotation.timeSec,
          timecode: canonicalAnnotation.anchorTimecode,
          frame: canonicalAnnotation.frameIndex,
          fps: annotationFps,
        },
        rawNote: canonicalAnnotation.rawNote,
        nextAction: canonicalAnnotation.nextAction,
        action: canonicalAnnotation.action,
        source: canonicalAnnotation.source,
        provenance: canonicalAnnotation.provenance,
        markerVisible: canonicalAnnotation.markerVisible,
      },
    });
    emitOmniCineToast("info", "Annotation published", "Saved structured editorial metadata for the current frame.");
    clearAnnotationDraftLifecycle({
      removeSuggestionId: editingSuggestionId,
      closePanel: overrides?.closeAfterPublish,
      reason: overrides?.closeAfterPublish ? "publish_and_close" : "publish",
    });
  }, [activeClipId, addAnnotation, annotationActionDraft, annotationAssetLabel, annotationDraft, annotationFps, annotationFrame, annotationKind, annotationSourceOverride, annotationSuggestionContext, annotationTimecode, annotationVoiceActive, clearAnnotationDraftLifecycle, currentTimeSec, editingSuggestionId, emitAnnotationMarkerProjection, emitVideoActionEvent, hasLinkedAudio, traceAnnotationSurface, updateAnnotation]);

  const handleDropAnnotationMarker = React.useCallback((overrides?: { note?: string; kind?: AnnotationSurfaceKind; source?: AnnotationCaptureSource }) => {
    const currentEditingAnnotationId = annotationUiStateRef.current.editingAnnotationId;
    const currentEditingAnnotationRecord = currentEditingAnnotationId
      ? annotationRecordsRef.current.find((record) => record.id === currentEditingAnnotationId) ?? null
      : null;
    const resolvedKind = overrides?.kind ?? annotationKind;
    const resolvedSource = overrides?.source ?? annotationSourceOverride ?? (annotationVoiceActive ? "voice" : "manual");
    const note = overrides?.note?.trim() || annotationDraft.trim() || `${annotationKindLabel(resolvedKind)} marker @ ${annotationTimecode}`;
    const canonicalAnnotation = resolveCanonicalAnnotationRecord({
      existingAnnotation: currentEditingAnnotationRecord,
      kind: resolvedKind,
      captureSource: resolvedSource,
      assetLabel: annotationAssetLabel,
      clipId: activeClipId ?? null,
      linkedAudio: hasLinkedAudio,
      frameIndex: annotationFrame,
      anchorTimecode: annotationTimecode,
      timeSec: currentTimeSec ?? null,
      rawNote: note,
      nextAction: annotationActionDraft.trim() || null,
      markerVisible: true,
      suggestionContext: annotationSuggestionContext,
    });

    if (currentEditingAnnotationRecord) {
      updateAnnotation(
        currentEditingAnnotationRecord.id,
        buildAnnotationUpdatePatch(canonicalAnnotation),
        { source: "user", note: "annotation surface marker update" },
      );
    } else {
      addAnnotation(canonicalAnnotation, { source: "user", note: "annotation surface marker drop" });
    }
    emitAnnotationMarkerProjection(canonicalAnnotation, "marker_only");
    traceAnnotationSurface("marker_drop", {
      reason: "annotation_surface",
      annotationType: resolvedKind,
      annotationSource: resolvedSource,
      hasNextAction: !!annotationActionDraft.trim(),
      editingAnnotation: !!currentEditingAnnotationRecord,
    });
    emitOmniCineToast("success", "Note marker added", `Dropped \"${note}\" on the timeline.`);
    clearAnnotationDraftLifecycle({ removeSuggestionId: editingSuggestionId, reason: "marker_drop" });
  }, [activeClipId, activeMode, activeSourceId, activeSourceLabel, addAnnotation, annotationActionDraft, annotationAssetLabel, annotationDraft, annotationFrame, annotationKind, annotationSourceOverride, annotationSuggestionContext, annotationTimecode, annotationVoiceActive, clearAnnotationDraftLifecycle, currentTimeSec, editingSuggestionId, emitAnnotationMarkerProjection, hasLinkedAudio, traceAnnotationSurface, updateAnnotation]);

  const handleAnnotationDraftChange = React.useCallback((value: string) => {
    const nextPatch: Partial<AnnotationUiState> = { draft: value };
    if (annotationVoiceActive) {
      const inferredKind = inferAnnotationKindFromVoice(value);
      if (inferredKind !== "marker") {
        nextPatch.kind = inferredKind;
      }
    }
    patchAnnotationUi(nextPatch);
  }, [annotationVoiceActive, patchAnnotationUi]);

  const handleAnnotationClose = React.useCallback(() => {
    traceAnnotationSurface("close_request", {
      reason: "surface_close",
    });
    patchAnnotationUi({ voiceActive: false, voiceInterim: "" });
    setVideoActionPanelOpen(null);
  }, [patchAnnotationUi, traceAnnotationSurface]);

  const getSuggestionById = React.useCallback(
    (suggestionId: string) => annotationAiSuggestions.find((suggestion) => suggestion.id === suggestionId) ?? null,
    [annotationAiSuggestions],
  );

  const loadSuggestionIntoDraft = React.useCallback(
    (suggestion: AnnotationSuggestion, disposition: AnnotationSuggestionContext["suggestionDisposition"]) => {
      const next = materializeSuggestionDraft(suggestion, disposition);
      patchAnnotationUi({
        kind: next.kind,
        draft: next.draftNote,
        actionDraft: next.draftNextAction,
        sourceOverride: next.sourceOverride,
        suggestionContext: next.suggestionContext,
      });
    },
    [patchAnnotationUi],
  );

  const handleAcceptAiSuggestion = React.useCallback((suggestionId: string) => {
    const suggestion = getSuggestionById(suggestionId);
    if (!suggestion) return;

    const next = materializeSuggestionDraft(suggestion, "accepted");
    patchAnnotationUi({
      kind: next.kind,
      draft: next.draftNote,
      actionDraft: next.draftNextAction,
      sourceOverride: next.sourceOverride,
      suggestionContext: next.suggestionContext,
      editingSuggestionId: null,
      aiSuggestions: annotationUiStateRef.current.aiSuggestions.filter((entry) => entry.id !== suggestionId),
      aiStatus: "Loaded AI suggestion into the draft.",
    });
  }, [getSuggestionById, patchAnnotationUi]);

  const handleTweakAiSuggestion = React.useCallback((suggestionId: string) => {
    const suggestion = getSuggestionById(suggestionId);
    if (!suggestion) return;

    const next = materializeSuggestionDraft(suggestion, "tweaked");
    patchAnnotationUi({
      kind: next.kind,
      draft: next.draftNote,
      actionDraft: next.draftNextAction,
      sourceOverride: next.sourceOverride,
      suggestionContext: next.suggestionContext,
      editingSuggestionId: suggestionId,
      aiStatus: "Editing AI suggestion in the draft.",
    });
  }, [getSuggestionById, patchAnnotationUi]);

  const handleMergeAiSuggestion = React.useCallback((suggestionId: string) => {
    const suggestion = getSuggestionById(suggestionId);
    if (!suggestion) return;

    const merged = mergeSuggestionIntoDraft(annotationDraft, annotationActionDraft, suggestion);

    patchAnnotationUi({
      kind: merged.kind,
      sourceOverride: merged.sourceOverride,
      suggestionContext: merged.suggestionContext,
      draft: merged.draftNote,
      actionDraft: merged.draftNextAction,
      editingSuggestionId: null,
      aiSuggestions: annotationUiStateRef.current.aiSuggestions.filter((entry) => entry.id !== suggestionId),
      aiStatus: "Merged AI suggestion into the current draft.",
    });
  }, [annotationActionDraft, annotationDraft, getSuggestionById, patchAnnotationUi]);

  const handleDiscardAiSuggestion = React.useCallback((suggestionId: string) => {
    patchAnnotationUi({
      aiSuggestions: annotationUiStateRef.current.aiSuggestions.filter((entry) => entry.id !== suggestionId),
      editingSuggestionId: annotationUiStateRef.current.editingSuggestionId === suggestionId ? null : annotationUiStateRef.current.editingSuggestionId,
      suggestionContext:
        annotationUiStateRef.current.suggestionContext?.suggestionId === suggestionId
          ? null
          : annotationUiStateRef.current.suggestionContext,
      aiStatus: "Discarded AI suggestion.",
    });
  }, [patchAnnotationUi]);

  const handleAskAiSuggestions = React.useCallback(() => {
    const contextualSummary = [narrativeSummary, annotationDraft.trim()].filter(Boolean).join(" ");
    const generated = runAiProposalBridge({
      timelineState,
      selectedClipId: activeClipId ?? null,
      proposals: compilerProposals,
      narrativeSummary: contextualSummary,
      groups,
      narrativeSonicsReadModel,
    });

    const filtered = generated.filter((suggestion) => suggestion.type === annotationKind);
    const suggestions = filtered.length > 0 ? filtered : generated;

    if (!suggestions.length) {
      patchAnnotationUi({ aiStatus: "No AI suggestions were generated for the current context." });
      return;
    }

    queueAiAnnotationSuggestions(suggestions, {
      openSurface: true,
      source: "panel",
    });
  }, [activeClipId, annotationDraft, annotationKind, compilerProposals, groups, narrativeSonicsReadModel, narrativeSummary, patchAnnotationUi, timelineState]);

  const appendAnnotationActionDraft = React.useCallback((value: string) => {
    const cleaned = value.trim();
    if (!cleaned) return;
    patchAnnotationUi({
      actionDraft: annotationUiStateRef.current.actionDraft.trim()
        ? `${annotationUiStateRef.current.actionDraft.trim()} ${cleaned}`
        : cleaned,
    });
  }, [patchAnnotationUi]);

  const appendAnnotationDraft = React.useCallback((value: string) => {
    const cleaned = value.trim();
    if (!cleaned) return;
    patchAnnotationUi({
      draft: annotationUiStateRef.current.draft.trim()
        ? `${annotationUiStateRef.current.draft.trim()} ${cleaned}`
        : cleaned,
    });
  }, [patchAnnotationUi]);

  const lastAnnotationVoiceErrorRef = React.useRef<string | null>(null);
  const {
    supported: annotationVoiceSupported,
    listening: annotationVoiceListening,
    error: annotationVoiceError,
    clearError: clearAnnotationVoiceError,
    start: startAnnotationVoice,
    stop: stopAnnotationVoice,
  } =
    useSpeechInput({
      preflightMicrophone: true,
      restartOnUnexpectedEnd: true,
      onFinal: (text) => patchAnnotationUi({ voiceFinal: text }),
      onInterim: (text) => patchAnnotationUi({ voiceInterim: text }),
    });

  const handleToggleAnnotationDictation = React.useCallback(async () => {
    if (!annotationVoiceSupported) {
      emitOmniCineToast("warning", "Dictation unavailable", "This browser does not expose live speech recognition for annotations.");
      return;
    }

    if (annotationVoiceListening) {
      stopAnnotationVoice();
      patchAnnotationUi({ voiceActive: false, voiceInterim: "" });
      return;
    }

    clearAnnotationVoiceError();
    const result = await startAnnotationVoice();
    if (!result.ok) {
      patchAnnotationUi({ voiceActive: false, voiceInterim: "" });
      emitOmniCineToast("warning", "Dictation unavailable", result.message ?? "Dictation could not start in this browser session.");
      return;
    }

    patchAnnotationUi({ voiceActive: true, voiceInterim: "" });
    emitOmniCineToast("info", "Dictation active", "Speak naturally. OmniCine will anchor the note to the current frame.");
  }, [annotationVoiceListening, annotationVoiceSupported, clearAnnotationVoiceError, patchAnnotationUi, startAnnotationVoice, stopAnnotationVoice]);

  React.useEffect(() => {
    const nextErrorKey = annotationVoiceError ? `${annotationVoiceError.code}:${annotationVoiceError.message}` : null;
    if (!nextErrorKey || lastAnnotationVoiceErrorRef.current === nextErrorKey) return;
    const errorMessage = annotationVoiceError?.message;
    if (!errorMessage) return;

    lastAnnotationVoiceErrorRef.current = nextErrorKey;
    patchAnnotationUi({ voiceActive: false, voiceInterim: "" });
    emitOmniCineToast("warning", "Dictation stopped", errorMessage);
  }, [annotationVoiceError, patchAnnotationUi]);

  React.useEffect(() => {
    if (annotationVoiceActive === annotationVoiceListening) return;
    patchAnnotationUi({ voiceActive: annotationVoiceListening });
  }, [annotationVoiceActive, annotationVoiceListening, patchAnnotationUi]);

  React.useEffect(() => {
    if (videoActionPanelOpen === "annotate") return;
    if (!annotationVoiceListening) return;

    stopAnnotationVoice();
    patchAnnotationUi({ voiceActive: false, voiceInterim: "" });
  }, [annotationVoiceListening, patchAnnotationUi, stopAnnotationVoice, videoActionPanelOpen]);

  React.useEffect(() => {
    if (!annotationVoiceFinal) return;

    const normalizeVoiceKind = (kind: AnnotationSurfaceKind | "marker" | "publish") => {
      if (kind === "marker" || kind === "publish") return annotationKind;
      return kind;
    };

    const parsed = parseAnnotationVoiceInput(annotationVoiceFinal);
    if (parsed.command === "close") {
      handleAnnotationClose();
    } else if (parsed.command === "publish_close") {
      handleEmitAnnotation({ closeAfterPublish: true });
    } else if (parsed.command === "publish") {
      handleEmitAnnotation();
    } else if (parsed.command === "action") {
      if (parsed.cleanedText) appendAnnotationActionDraft(parsed.cleanedText);
    } else if (parsed.command === "marker") {
      const resolvedKind = normalizeVoiceKind(parsed.kind);
      setAnnotationKind(resolvedKind);
      handleDropAnnotationMarker({ note: parsed.cleanedText, kind: resolvedKind, source: "voice" });
    } else if (parsed.command === "draft") {
      if (parsed.kind !== "publish") setAnnotationKind(normalizeVoiceKind(parsed.kind));
      if (parsed.cleanedText) appendAnnotationDraft(parsed.cleanedText);
    }

    patchAnnotationUi({ voiceFinal: null, voiceInterim: "" });
  }, [annotationKind, annotationVoiceFinal, appendAnnotationActionDraft, appendAnnotationDraft, handleAnnotationClose, handleDropAnnotationMarker, handleEmitAnnotation, patchAnnotationUi]);

  const handlePublishAnnotationAndClose = React.useCallback(() => {
    handleEmitAnnotation({ closeAfterPublish: true });
  }, [handleEmitAnnotation]);

  const handleStagePublishPreview = React.useCallback(() => {
    const note = publishDraft.trim();
    const handoffAnnotations = activeAnnotationRecords.map((annotation) => ({
      type: annotation.type,
      asset: annotation.assetLabel,
      timeSec: annotation.timeSec,
      anchorTimecode: annotation.anchorTimecode,
      frameIndex: annotation.frameIndex,
      rawNote: annotation.rawNote,
      action: annotation.action,
      source: annotation.source,
    }));
    emitVideoOpsMetric("omnicine.video.publish.preview", "normal", 1, OMNICINE_VIDEO_PUBLISH_EVENT);
    emitVideoOpsTimeline("video_publish_preview", {
      mediaType,
      focusScope: activeMode,
      hasNote: note.length > 0,
      noteLength: note.length,
      linkedAudio: hasLinkedAudio,
      linkedVideo: hasLinkedVideo,
      annotationCount: handoffAnnotations.length,
    });
    emitVideoActionEvent(OMNICINE_VIDEO_PUBLISH_EVENT, {
      stage: "preview",
      note: note || null,
      bindingMode: bindingMode ?? "unbound",
      linkedAudio: hasLinkedAudio,
      linkedVideo: hasLinkedVideo,
      annotations: handoffAnnotations,
    });
    emitVideoActionEvent(OMNICINE_VIDEO_PUBLISH_RESULT_EVENT, { status: "ok", stage: "preview" });
    emitOmniCineToast("info", "Publish preview staged", "Queued a witness-safe publish preview event for the focused clip.");
  }, [activeAnnotationRecords, activeMode, bindingMode, emitVideoActionEvent, hasLinkedAudio, hasLinkedVideo, mediaType, publishDraft]);

  const handlePublishClip = React.useCallback(() => {
    const note = publishDraft.trim();
    const handoffAnnotations = activeAnnotationRecords.map((annotation) => ({
      type: annotation.type,
      asset: annotation.assetLabel,
      timeSec: annotation.timeSec,
      anchorTimecode: annotation.anchorTimecode,
      frameIndex: annotation.frameIndex,
      rawNote: annotation.rawNote,
      action: annotation.action,
      source: annotation.source,
    }));
    emitVideoOpsMetric("omnicine.video.publish.commit", "normal", 1, OMNICINE_VIDEO_PUBLISH_EVENT);
    emitVideoOpsTimeline("video_publish_commit", {
      mediaType,
      focusScope: activeMode,
      hasNote: note.length > 0,
      noteLength: note.length,
      linkedAudio: hasLinkedAudio,
      linkedVideo: hasLinkedVideo,
      annotationCount: handoffAnnotations.length,
    });
    emitVideoActionEvent(OMNICINE_VIDEO_PUBLISH_EVENT, {
      stage: "publish",
      note: note || null,
      bindingMode: bindingMode ?? "unbound",
      linkedAudio: hasLinkedAudio,
      linkedVideo: hasLinkedVideo,
      annotations: handoffAnnotations,
    });
    emitVideoActionEvent(OMNICINE_VIDEO_PUBLISH_RESULT_EVENT, { status: "ok", stage: "publish" });
    emitOmniCineToast("success", "Publish intent emitted", "Issued a publish intent event for the focused clip.");
    setPublishDraft("");
  }, [activeAnnotationRecords, activeMode, bindingMode, emitVideoActionEvent, hasLinkedAudio, hasLinkedVideo, mediaType, publishDraft]);

  const witnessGlassTone = mediaType === "audio"
    ? {
        hostLight: "linear-gradient(145deg, rgba(248,250,252,0.38), rgba(255,255,255,0.20) 24%, rgba(209,250,229,0.18) 62%, rgba(236,253,245,0.26)), radial-gradient(circle at top left, rgba(16,185,129,0.14), rgba(16,185,129,0) 34%), radial-gradient(circle at 78% 18%, rgba(45,212,191,0.10), rgba(45,212,191,0) 28%)",
        hostDark: "linear-gradient(145deg, rgba(2,6,23,0.58), rgba(15,23,42,0.38) 24%, rgba(16,185,129,0.16) 66%, rgba(45,212,191,0.12)), radial-gradient(circle at top left, rgba(45,212,191,0.16), rgba(45,212,191,0) 34%), radial-gradient(circle at 80% 16%, rgba(110,231,183,0.10), rgba(110,231,183,0) 28%)",
        panelLight: "linear-gradient(145deg, rgba(248,250,252,0.74), rgba(255,255,255,0.48) 24%, rgba(209,250,229,0.34) 68%, rgba(236,253,245,0.58)), radial-gradient(circle at top left, rgba(16,185,129,0.18), rgba(16,185,129,0) 34%), radial-gradient(circle at 78% 18%, rgba(45,212,191,0.12), rgba(45,212,191,0) 28%)",
        panelDark: "linear-gradient(145deg, rgba(2,6,23,0.72), rgba(15,23,42,0.54) 24%, rgba(16,185,129,0.20) 68%, rgba(45,212,191,0.14)), radial-gradient(circle at top left, rgba(45,212,191,0.18), rgba(45,212,191,0) 34%), radial-gradient(circle at 80% 16%, rgba(110,231,183,0.12), rgba(110,231,183,0) 28%)",
        labelLightBg: "linear-gradient(180deg, rgba(209,250,229,0.92), rgba(167,243,208,0.72))",
        labelDarkBg: "linear-gradient(180deg, rgba(6,78,59,0.88), rgba(4,120,87,0.78))",
        labelLightText: "rgba(6,78,59,0.96)",
        labelDarkText: "rgba(236,253,245,0.96)",
      }
    : mediaType === "image"
      ? {
          hostLight: "linear-gradient(145deg, rgba(248,250,252,0.38), rgba(255,255,255,0.20) 24%, rgba(224,231,255,0.20) 62%, rgba(245,243,255,0.28)), radial-gradient(circle at top left, rgba(129,140,248,0.14), rgba(129,140,248,0) 34%), radial-gradient(circle at 78% 18%, rgba(168,85,247,0.10), rgba(168,85,247,0) 28%)",
          hostDark: "linear-gradient(145deg, rgba(2,6,23,0.58), rgba(15,23,42,0.38) 24%, rgba(99,102,241,0.18) 66%, rgba(168,85,247,0.12)), radial-gradient(circle at top left, rgba(129,140,248,0.16), rgba(129,140,248,0) 34%), radial-gradient(circle at 80% 16%, rgba(196,181,253,0.10), rgba(196,181,253,0) 28%)",
          panelLight: "linear-gradient(145deg, rgba(248,250,252,0.74), rgba(255,255,255,0.48) 24%, rgba(224,231,255,0.36) 68%, rgba(245,243,255,0.58)), radial-gradient(circle at top left, rgba(129,140,248,0.18), rgba(129,140,248,0) 34%), radial-gradient(circle at 78% 18%, rgba(168,85,247,0.12), rgba(168,85,247,0) 28%)",
          panelDark: "linear-gradient(145deg, rgba(2,6,23,0.72), rgba(15,23,42,0.54) 24%, rgba(99,102,241,0.22) 68%, rgba(168,85,247,0.14)), radial-gradient(circle at top left, rgba(129,140,248,0.18), rgba(129,140,248,0) 34%), radial-gradient(circle at 80% 16%, rgba(196,181,253,0.12), rgba(196,181,253,0) 28%)",
          labelLightBg: "linear-gradient(180deg, rgba(224,231,255,0.92), rgba(196,181,253,0.72))",
          labelDarkBg: "linear-gradient(180deg, rgba(67,56,202,0.86), rgba(109,40,217,0.78))",
          labelLightText: "rgba(49,46,129,0.96)",
          labelDarkText: "rgba(245,243,255,0.96)",
        }
      : {
          hostLight: "linear-gradient(145deg, rgba(248,250,252,0.38), rgba(255,255,255,0.20) 24%, rgba(186,230,253,0.18) 62%, rgba(240,249,255,0.26)), radial-gradient(circle at top left, rgba(56,189,248,0.14), rgba(56,189,248,0) 34%), radial-gradient(circle at 78% 18%, rgba(45,212,191,0.10), rgba(45,212,191,0) 28%)",
          hostDark: "linear-gradient(145deg, rgba(2,6,23,0.58), rgba(15,23,42,0.38) 24%, rgba(8,145,178,0.18) 66%, rgba(45,212,191,0.12)), radial-gradient(circle at top left, rgba(34,211,238,0.16), rgba(34,211,238,0) 34%), radial-gradient(circle at 80% 16%, rgba(45,212,191,0.12), rgba(45,212,191,0) 28%)",
          panelLight: "linear-gradient(145deg, rgba(248,250,252,0.74), rgba(255,255,255,0.48) 24%, rgba(186,230,253,0.34) 68%, rgba(240,249,255,0.58)), radial-gradient(circle at top left, rgba(56,189,248,0.18), rgba(56,189,248,0) 34%), radial-gradient(circle at 78% 18%, rgba(45,212,191,0.12), rgba(45,212,191,0) 28%)",
          panelDark: "linear-gradient(145deg, rgba(2, 6, 23, 0.72), rgba(15, 23, 42, 0.54) 24%, rgba(8, 145, 178, 0.22) 68%, rgba(45, 212, 191, 0.14)), radial-gradient(circle at top left, rgba(34,211,238,0.18), rgba(34,211,238,0) 34%), radial-gradient(circle at 80% 16%, rgba(45,212,191,0.12), rgba(45,212,191,0) 28%)",
          labelLightBg: "linear-gradient(180deg, rgba(186,230,253,0.92), rgba(125,211,252,0.72))",
          labelDarkBg: "linear-gradient(180deg, rgba(8,47,73,0.88), rgba(3,105,161,0.78))",
          labelLightText: "rgba(12,74,110,0.96)",
          labelDarkText: "rgba(240,249,255,0.96)",
        };

  const actionSurfaceTone = videoActionPanelPresence === "publish"
    ? {
        panelLight: "linear-gradient(145deg, rgba(255,251,235,0.80), rgba(255,255,255,0.48) 24%, rgba(254,240,138,0.32) 68%, rgba(255,247,237,0.54)), radial-gradient(circle at top left, rgba(251,191,36,0.18), rgba(251,191,36,0) 34%), radial-gradient(circle at 78% 18%, rgba(249,115,22,0.12), rgba(249,115,22,0) 28%)",
        panelDark: "linear-gradient(145deg, rgba(2,6,23,0.72), rgba(15,23,42,0.54) 24%, rgba(245,158,11,0.20) 68%, rgba(249,115,22,0.12)), radial-gradient(circle at top left, rgba(251,191,36,0.18), rgba(251,191,36,0) 34%), radial-gradient(circle at 80% 16%, rgba(249,115,22,0.12), rgba(249,115,22,0) 28%)",
        chipLightBg: "linear-gradient(180deg, rgba(254,240,138,0.94), rgba(251,191,36,0.78))",
        chipDarkBg: "linear-gradient(180deg, rgba(120,53,15,0.88), rgba(180,83,9,0.78))",
        chipLightText: "rgba(120,53,15,0.96)",
        chipDarkText: "rgba(255,251,235,0.96)",
      }
    : videoActionPanelPresence === "frame"
      ? {
          panelLight: "linear-gradient(145deg, rgba(238,242,255,0.80), rgba(255,255,255,0.48) 24%, rgba(191,219,254,0.32) 68%, rgba(239,246,255,0.54)), radial-gradient(circle at top left, rgba(96,165,250,0.18), rgba(96,165,250,0) 34%), radial-gradient(circle at 78% 18%, rgba(129,140,248,0.12), rgba(129,140,248,0) 28%)",
          panelDark: "linear-gradient(145deg, rgba(2,6,23,0.72), rgba(15,23,42,0.54) 24%, rgba(96,165,250,0.20) 68%, rgba(129,140,248,0.12)), radial-gradient(circle at top left, rgba(96,165,250,0.18), rgba(96,165,250,0) 34%), radial-gradient(circle at 80% 16%, rgba(129,140,248,0.12), rgba(129,140,248,0) 28%)",
          chipLightBg: "linear-gradient(180deg, rgba(191,219,254,0.94), rgba(96,165,250,0.78))",
          chipDarkBg: "linear-gradient(180deg, rgba(30,64,175,0.88), rgba(37,99,235,0.78))",
          chipLightText: "rgba(30,64,175,0.96)",
          chipDarkText: "rgba(239,246,255,0.96)",
        }
      : videoActionPanelPresence === "export"
        ? {
            panelLight: "linear-gradient(145deg, rgba(240,253,250,0.80), rgba(255,255,255,0.48) 24%, rgba(167,243,208,0.32) 68%, rgba(236,253,245,0.54)), radial-gradient(circle at top left, rgba(16,185,129,0.18), rgba(16,185,129,0) 34%), radial-gradient(circle at 78% 18%, rgba(45,212,191,0.12), rgba(45,212,191,0) 28%)",
            panelDark: "linear-gradient(145deg, rgba(2,6,23,0.72), rgba(15,23,42,0.54) 24%, rgba(16,185,129,0.20) 68%, rgba(45,212,191,0.12)), radial-gradient(circle at top left, rgba(16,185,129,0.18), rgba(16,185,129,0) 34%), radial-gradient(circle at 80% 16%, rgba(45,212,191,0.12), rgba(45,212,191,0) 28%)",
            chipLightBg: "linear-gradient(180deg, rgba(167,243,208,0.94), rgba(52,211,153,0.78))",
            chipDarkBg: "linear-gradient(180deg, rgba(6,78,59,0.88), rgba(5,150,105,0.78))",
            chipLightText: "rgba(6,78,59,0.96)",
            chipDarkText: "rgba(236,253,245,0.96)",
          }
        : videoActionPanelPresence === "annotate"
          ? {
              panelLight: "linear-gradient(145deg, rgba(250,245,255,0.80), rgba(255,255,255,0.48) 24%, rgba(233,213,255,0.30) 68%, rgba(245,243,255,0.56)), radial-gradient(circle at top left, rgba(192,132,252,0.18), rgba(192,132,252,0) 34%), radial-gradient(circle at 78% 18%, rgba(236,72,153,0.10), rgba(236,72,153,0) 28%)",
              panelDark: "linear-gradient(145deg, rgba(2,6,23,0.72), rgba(15,23,42,0.54) 24%, rgba(168,85,247,0.20) 68%, rgba(236,72,153,0.12)), radial-gradient(circle at top left, rgba(192,132,252,0.18), rgba(192,132,252,0) 34%), radial-gradient(circle at 80% 16%, rgba(236,72,153,0.12), rgba(236,72,153,0) 28%)",
              chipLightBg: "linear-gradient(180deg, rgba(233,213,255,0.94), rgba(192,132,252,0.78))",
              chipDarkBg: "linear-gradient(180deg, rgba(107,33,168,0.88), rgba(168,85,247,0.76))",
              chipLightText: "rgba(88,28,135,0.96)",
              chipDarkText: "rgba(250,245,255,0.96)",
            }
          : {
              panelLight: witnessGlassTone.panelLight,
              panelDark: witnessGlassTone.panelDark,
              chipLightBg: witnessGlassTone.labelLightBg,
              chipDarkBg: witnessGlassTone.labelDarkBg,
              chipLightText: witnessGlassTone.labelLightText,
              chipDarkText: witnessGlassTone.labelDarkText,
            };

  const titleScrimStyle = prefersLightMode
    ? {
        background: "linear-gradient(180deg, rgba(255,255,255,0.26), rgba(255,255,255,0.12))",
        border: "1px solid rgba(148,163,184,0.16)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.44)",
      }
    : {
        background: "linear-gradient(180deg, rgba(2,6,23,0.30), rgba(15,23,42,0.18))",
        border: "1px solid rgba(186,230,253,0.10)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
      };

  const focusTitleChipStyle = {
    background: prefersLightMode ? witnessGlassTone.labelLightBg : witnessGlassTone.labelDarkBg,
    color: prefersLightMode ? witnessGlassTone.labelLightText : witnessGlassTone.labelDarkText,
    boxShadow: prefersLightMode ? "0 8px 18px rgba(15,23,42,0.10)" : "0 10px 24px rgba(2,6,23,0.28)",
  };

  if (!visible) return null;

  return (
    <div
      data-oc-audio-witness-bar="1"
      data-oc-clip-focus-glass-panel="1"
      className={isArcLensPrimary ? "relative z-[56] mx-auto flex w-full max-w-full flex-col overflow-visible rounded-[24px] px-4 py-1" : "relative z-[56] mx-auto flex w-full max-w-full flex-col overflow-visible rounded-[24px] px-4 py-2"}
      tabIndex={0}
      role="region"
      aria-label={`${mediaFocusLabel} glass panel`}
      onKeyDown={(event) => {
        const key = event.key.toLowerCase();
        if (key === "escape") {
          event.preventDefault();
          onClose();
          return;
        }

        if (isEditableKeyboardTarget(event.target)) {
          return;
        }

        if (key === "a") onToggleLens("arc");
        if (key === "f") onToggleLens("fx");
        if (key === "c") onToggleLens("continuity");
        if (key === "p") onToggleLens("aiPreview");
        if (key === "s" && isVideoFocus) setVideoActionPanelOpen((prev) => (prev === "share" ? null : "share"));
      }}
      style={{
        background: prefersLightMode
          ? witnessGlassTone.hostLight
          : witnessGlassTone.hostDark,
        border: prefersLightMode ? "1px solid rgba(148,163,184,0.20)" : "1px solid rgba(186, 230, 253, 0.16)",
        boxShadow: prefersLightMode
          ? "inset 0 1px 0 rgba(255,255,255,0.68), inset 0 -1px 0 rgba(255,255,255,0.10), 0 18px 48px rgba(148,163,184,0.16)"
          : "inset 0 1px 0 rgba(255,255,255,0.10), inset 0 -1px 0 rgba(15,23,42,0.24), 0 22px 56px rgba(2,6,23,0.34)",
        backdropFilter: "blur(24px) saturate(1.28)",
        WebkitBackdropFilter: "blur(24px) saturate(1.28)",
        opacity: entered ? 1 : 0,
        transform: entered ? "translateY(0px)" : "translateY(14px)",
        transition: "opacity 220ms ease, transform 240ms ease, box-shadow 180ms ease",
        maxHeight: isArcLensPrimary ? 72 : AUDIO_WITNESS_MAX_HEIGHT_PX,
      }}
    >
      <div className="pointer-events-none absolute inset-0 opacity-80" aria-hidden>
        <div
          className="absolute inset-x-0 top-0 h-px"
          style={{
            background: prefersLightMode
              ? "linear-gradient(90deg, rgba(255,255,255,0), rgba(56,189,248,0.48), rgba(255,255,255,0))"
              : "linear-gradient(90deg, rgba(255,255,255,0), rgba(186,230,253,0.36), rgba(255,255,255,0))",
          }}
        />
        <div
          className="absolute -left-10 top-0 h-24 w-40 rounded-full"
          style={{
            background: prefersLightMode
              ? "radial-gradient(circle, rgba(56,189,248,0.14), rgba(56,189,248,0))"
              : "radial-gradient(circle, rgba(34,211,238,0.12), rgba(34,211,238,0))",
          }}
        />
      </div>

      <div className={isArcLensPrimary ? "relative flex flex-nowrap items-center gap-1.5" : "relative flex flex-nowrap items-center gap-2.5"}>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className={isArcLensPrimary ? "flex min-w-0 items-center gap-1.5 rounded-[14px] px-2 py-0.5" : "flex min-w-0 items-center gap-2 rounded-[16px] px-2 py-1.5"} style={titleScrimStyle}>
            <div className={`min-w-0 truncate ${isArcLensPrimary ? "text-[12px]" : "text-[13px]"} font-semibold tracking-[0.01em] ${prefersLightMode ? "text-slate-950/96" : "text-white/98"}`}>
              {title}
            </div>
            <span
              className={`inline-flex shrink-0 items-center rounded-full px-2 font-black uppercase tracking-[0.16em] ${isArcLensPrimary ? "h-4.5 text-[8px]" : "h-5 text-[9px]"}`}
              style={focusTitleChipStyle}
            >
              {mediaFocusLabel}
            </span>
          </div>
        </div>

        {isArcLensPrimary ? (
          <div className="flex min-w-0 flex-[0_1_420px] items-center justify-center gap-1 overflow-x-auto overflow-y-hidden pb-0.5">
            <div
              className="rounded-full px-2 py-0.5 text-[8px] font-semibold uppercase tracking-[0.14em]"
              style={{
                border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
                background: prefersLightMode ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.05)",
                color: prefersLightMode ? "rgba(51,65,85,0.78)" : "rgba(255,255,255,0.66)",
                flex: "0 0 auto",
              }}
            >
              {arcAuditionSummary} · {arcModeSummary}
            </div>
            <div
              className="rounded-full px-2 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em]"
              style={{
                border: prefersLightMode ? "1px solid rgba(14,165,233,0.18)" : "1px solid rgba(34,211,238,0.14)",
                background: prefersLightMode ? "rgba(186,230,253,0.16)" : "rgba(8,145,178,0.10)",
                color: prefersLightMode ? "rgba(12,74,110,0.88)" : "rgba(165,243,252,0.84)",
                flex: "0 0 auto",
              }}
            >
              {arcBindingSummary}
            </div>
          </div>
        ) : (
          <div className="hidden min-w-0 flex-[0_1_420px] items-center justify-center gap-2 overflow-hidden xl:flex">
            <div
              className="rounded-full px-2.5 py-0.5 text-[10px] font-semibold"
              style={{
                border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.10)",
                background: prefersLightMode ? "rgba(255,255,255,0.28)" : "rgba(255,255,255,0.06)",
                color: prefersLightMode ? "rgba(15,23,42,0.84)" : "rgba(255,255,255,0.86)",
              }}
            >
              {fmt(currentTimeSec)} / {fmt(durationSec)}
            </div>
            <div
              className="rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em]"
              style={{
                border: prefersLightMode ? "1px solid rgba(14,165,233,0.2)" : "1px solid rgba(186,230,253,0.12)",
                background: prefersLightMode ? "rgba(186,230,253,0.22)" : "rgba(103,232,249,0.08)",
                color: prefersLightMode ? "rgba(12,74,110,0.92)" : "rgba(236,254,255,0.78)",
              }}
            >
              {status}
            </div>
            {showAnnotationHeaderContext ? (
              <>
                <div
                  className="shrink-0 rounded-full px-2.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em]"
                  style={{
                    border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
                    background: prefersLightMode ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.04)",
                    color: prefersLightMode ? "rgba(51,65,85,0.78)" : "rgba(255,255,255,0.62)",
                  }}
                >
                  Audio {hasLinkedAudio ? "Linked" : "Unlinked"}
                </div>
                <div
                  className="shrink-0 rounded-full px-2.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em]"
                  style={{
                    border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
                    background: prefersLightMode ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.04)",
                    color: prefersLightMode ? "rgba(51,65,85,0.78)" : "rgba(255,255,255,0.62)",
                  }}
                >
                  Time {annotationTimecode}
                </div>
              </>
            ) : null}
          </div>
        )}

        <div className="flex min-w-0 shrink items-center justify-end gap-1.5 overflow-hidden">
          {isVideoFocus && !isArcLensPrimary ? (
            <div
              ref={videoActionToggleRowRef}
              className="flex min-w-0 flex-nowrap items-center gap-1 overflow-x-auto overflow-y-hidden pb-0.5"
              style={{ scrollbarWidth: "thin" }}
            >
              {([
                ["share", "SHARE"],
                ["frame", "FRAME"],
                ["export", "EXPORT"],
                ["publish", "PUBLISH"],
                ["annotate", "ANNOTATE"],
              ] as const).map(([panelId, label]) => {
                const isActive = videoActionPanelOpen === panelId;
                return (
                  <button
                    key={panelId}
                    type="button"
                    onClick={() => {
                      setBindingPanelOpen(false);
                      if (panelId === "annotate") {
                        traceAnnotationSurface(isActive ? "close_request" : "open_request", {
                          reason: isActive ? "toggle_button" : "toggle_button",
                        });
                      }
                      setVideoActionPanelOpen((prev) => (prev === panelId ? null : panelId));
                    }}
                    className="inline-flex h-7 shrink-0 items-center rounded-full px-2.5 text-[9px] font-black tracking-[0.16em]"
                    aria-pressed={isActive}
                    aria-label={`Open ${label.toLowerCase()} video panel`}
                    style={{
                      background: isActive
                        ? prefersLightMode
                          ? "rgba(186,230,253,0.28)"
                          : "rgba(56,189,248,0.22)"
                        : prefersLightMode
                          ? "rgba(255,255,255,0.22)"
                          : "rgba(255,255,255,0.08)",
                      border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
                      color: isActive
                        ? prefersLightMode
                          ? "rgba(12,74,110,0.96)"
                          : "rgba(207,250,254,0.98)"
                        : prefersLightMode
                          ? "rgba(30,41,59,0.76)"
                          : "rgba(255,255,255,0.74)",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          ) : null}

          <button
            ref={bindingToggleRef}
            type="button"
            onClick={() => setBindingPanelOpen((prev) => !prev)}
            className="inline-flex h-7 shrink-0 items-center rounded-full px-2.5 text-[9px] font-black tracking-[0.16em]"
            aria-label="Toggle media binding popover"
            aria-expanded={bindingPanelOpen}
            aria-controls="oc-audio-witness-binding-popover"
            style={{
              background: bindingPanelOpen || hasExplicitBinding
                ? prefersLightMode
                  ? "rgba(186,230,253,0.26)"
                  : "rgba(34,211,238,0.18)"
                : prefersLightMode
                  ? "rgba(255,255,255,0.22)"
                  : "rgba(255,255,255,0.08)",
              border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
              color: bindingPanelOpen || hasExplicitBinding
                ? prefersLightMode
                  ? "rgba(12,74,110,0.96)"
                  : "rgba(207,250,254,0.98)"
                : prefersLightMode
                  ? "rgba(30,41,59,0.76)"
                  : "rgba(255,255,255,0.74)",
            }}
          >
            {hasExplicitBinding ? "BIND" : "LINK"}
          </button>

          <NarrativeSonicsLensTags
            availableLensIds={availableLensIds}
            activeLensId={activeLensId}
            onToggleLens={onToggleLens}
            lightMode={prefersLightMode}
          />

          <button
            type="button"
            className="inline-flex h-7 shrink-0 items-center rounded-full px-2.5 text-[9px] font-black tracking-[0.16em]"
            disabled={!activeLensId}
            onClick={() => {
              if (activeLensId) onPinMiniLens(activeLensId);
            }}
            style={{
              background: pinnedMiniLensId
                ? prefersLightMode
                  ? "rgba(186,230,253,0.26)"
                  : "rgba(34,211,238,0.18)"
                : prefersLightMode
                  ? "rgba(255,255,255,0.22)"
                  : "rgba(255,255,255,0.08)",
              border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
              color: pinnedMiniLensId
                ? prefersLightMode
                  ? "rgba(12,74,110,0.96)"
                  : "rgba(207,250,254,0.98)"
                : prefersLightMode
                  ? "rgba(30,41,59,0.76)"
                  : "rgba(255,255,255,0.74)",
              opacity: activeLensId ? 1 : 0.54,
            }}
            aria-label={activeLensId ? `Pin ${activeLensId} mini lens` : "Pin mini lens"}
          >
            {pinnedMiniLensId ? "PINNED" : "PIN"}
          </button>

          {onRemoveActiveClip && !isArcLensPrimary ? (
            <button
              type="button"
              onClick={onRemoveActiveClip}
              className="inline-flex h-7 shrink-0 items-center rounded-full px-2.5 text-[9px] font-black tracking-[0.16em]"
              aria-label={`Remove imported ${mediaType} clip`}
              style={{
                background: prefersLightMode
                  ? "linear-gradient(180deg, rgba(254,226,226,0.28), rgba(255,255,255,0.16))"
                  : "linear-gradient(180deg, rgba(127,29,29,0.34), rgba(15,23,42,0.76))",
                border: prefersLightMode ? "1px solid rgba(248,113,113,0.18)" : "1px solid rgba(248,113,113,0.18)",
                color: prefersLightMode ? "rgba(127,29,29,0.92)" : "rgba(254,226,226,0.86)",
              }}
            >
              REMOVE
            </button>
          ) : null}

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 shrink-0 items-center rounded-full px-2.5 text-[9px] font-black tracking-[0.16em]"
            aria-label="Close clip focus panel"
            style={{
              background: prefersLightMode ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.08)",
              border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
              color: prefersLightMode ? "rgba(15,23,42,0.82)" : "rgba(255,255,255,0.8)",
            }}
          >
            CLOSE
          </button>
        </div>

        {bindingPanelOpen ? (
          <div
            id="oc-audio-witness-binding-popover"
            ref={bindingPopoverRef}
            className="absolute right-0 bottom-[calc(100%+12px)] z-[70] w-[min(calc(100vw-2rem),68rem)] max-w-none"
          >
            <div
              className="overflow-hidden rounded-[20px]"
              style={{
                background: prefersLightMode
                  ? witnessGlassTone.panelLight
                  : witnessGlassTone.panelDark,
                border: prefersLightMode ? "1px solid rgba(148,163,184,0.22)" : "1px solid rgba(186,230,253,0.18)",
                boxShadow: prefersLightMode
                  ? "inset 0 1px 0 rgba(255,255,255,0.72), 0 18px 44px rgba(148,163,184,0.20)"
                  : "inset 0 1px 0 rgba(255,255,255,0.10), inset 0 -1px 0 rgba(15,23,42,0.20), 0 22px 52px rgba(2,6,23,0.34)",
                backdropFilter: "blur(24px) saturate(1.28)",
                WebkitBackdropFilter: "blur(24px) saturate(1.28)",
              }}
            >
              <div
                className="pointer-events-none absolute inset-0 opacity-90"
                aria-hidden
                style={{
                  background: prefersLightMode
                    ? "linear-gradient(180deg, rgba(255,255,255,0.14), rgba(255,255,255,0.02) 24%, rgba(255,255,255,0) 100%)"
                    : "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02) 24%, rgba(255,255,255,0) 100%)",
                }}
              />
              <div className="overflow-visible">
                <MediaBindingControls
                  variant="glass"
                  bindingMode={bindingMode ?? "unbound"}
                  hasExplicitBinding={hasExplicitBinding}
                  familyOptions={familyOptions}
                  selectedFamilyId={selectedFamilyId}
                  audioMembers={audioMembers}
                  videoMembers={videoMembers}
                  selectedAudioMemberId={selectedAudioMemberId}
                  selectedVideoMemberId={selectedVideoMemberId}
                  onSelectFamily={onSelectFamily}
                  onSelectAudioMember={onSelectAudioMember}
                  onSelectVideoMember={onSelectVideoMember}
                  onInspectMember={onInspectMediaMember}
                  onUseCurrentProjection={canBindCurrentFamily ? onBindCurrentFamily : undefined}
                  onClearExplicitBinding={onClearExplicitBinding}
                />
              </div>
            </div>
          </div>
        ) : null}

        {videoActionPanelPresence ? (
          <div
            ref={videoActionPopoverRef}
            className="absolute z-[80]"
            style={
              videoActionPanelPresence === "annotate"
                ? {
                    left: 0,
                    right: 0,
                    width: "auto",
                    maxWidth: "none",
                    bottom: "calc(100% + 12px)",
                    transform: videoActionPopoverOffsetY ? `translateY(${videoActionPopoverOffsetY}px)` : undefined,
                  }
                : {
                    right: 0,
                    bottom: "calc(100% + 12px)",
                    width: "min(100%, 34rem)",
                    maxWidth: "34rem",
                    transform: videoActionPopoverOffsetY ? `translateY(${videoActionPopoverOffsetY}px)` : undefined,
                  }
            }
          >
            <div
              className={`${videoActionPanelPresence === "annotate" ? "overflow-hidden" : "overflow-hidden"} rounded-[20px] px-4 py-3`}
              style={{
                background: prefersLightMode
                  ? actionSurfaceTone.panelLight
                  : actionSurfaceTone.panelDark,
                border: prefersLightMode ? "1px solid rgba(148,163,184,0.22)" : "1px solid rgba(186,230,253,0.18)",
                boxShadow: prefersLightMode
                  ? "inset 0 1px 0 rgba(255,255,255,0.72), 0 18px 44px rgba(148,163,184,0.20)"
                  : "inset 0 1px 0 rgba(255,255,255,0.10), inset 0 -1px 0 rgba(15,23,42,0.20), 0 22px 52px rgba(2,6,23,0.34)",
                backdropFilter: "blur(24px) saturate(1.28)",
                WebkitBackdropFilter: "blur(24px) saturate(1.28)",
                opacity: videoActionPanelEntered ? 1 : 0,
                transform: videoActionPanelEntered ? "translateY(0px) scale(1)" : "translateY(12px) scale(0.985)",
                transformOrigin: videoActionPanelPresence === "annotate" ? "center bottom" : "right bottom",
                transition: "opacity 180ms ease, transform 240ms cubic-bezier(0.22,1,0.36,1)",
                pointerEvents: videoActionPanelOpen ? "auto" : "none",
                maxHeight: videoActionPanelPresence === "annotate" ? ANNOTATION_PANEL_MAX_VIEWPORT_HEIGHT : undefined,
              }}
            >
              {videoActionPanelPresence === "annotate" ? (
                <div
                  className="min-h-0 overflow-y-auto pr-1"
                  style={{
                    maxHeight: `calc(${ANNOTATION_PANEL_MAX_VIEWPORT_HEIGHT} - 1.5rem)`,
                    scrollbarGutter: "stable both-edges",
                    overscrollBehavior: "contain",
                  }}
                >
                  <AnnotationSurface
                    draftState={annotationDraftState}
                    validationState={annotationValidationState}
                    suggestionViewState={annotationSuggestionViewState}
                    handoffContext={annotationHandoffContext}
                    isDictating={annotationVoiceActive}
                    dictationSupported={annotationVoiceSupported}
                    dictationStatus={
                      annotationVoiceError?.message ??
                      (annotationVoiceActive
                        ? "Listening now. Say action: to route the next step instead of the main note."
                        : "Use action: in dictation to route the next step.")
                    }
                    interimTranscript={annotationVoiceInterim}
                    aiStatus={annotationAiStatus}
                    aiSuggestions={annotationAiSuggestions}
                    prefersLightMode={prefersLightMode}
                    onDraftChange={handleAnnotationDraftChange}
                    onActionDraftChange={setAnnotationActionDraft}
                    onSelectKind={setAnnotationKind}
                    onAskAi={handleAskAiSuggestions}
                    onAcceptSuggestion={handleAcceptAiSuggestion}
                    onTweakSuggestion={handleTweakAiSuggestion}
                    onMergeSuggestion={handleMergeAiSuggestion}
                    onDiscardSuggestion={handleDiscardAiSuggestion}
                    onToggleDictation={handleToggleAnnotationDictation}
                    onDropMarker={handleDropAnnotationMarker}
                    onPublish={handleEmitAnnotation}
                    onPublishAndClose={handlePublishAnnotationAndClose}
                    onClose={handleAnnotationClose}
                  />
                </div>
              ) : (
                <>
                  <div
                    className="inline-flex h-7 items-center self-start rounded-full px-3 text-[10px] font-black uppercase tracking-[0.2em]"
                    style={{
                      background: prefersLightMode ? actionSurfaceTone.chipLightBg : actionSurfaceTone.chipDarkBg,
                      color: prefersLightMode ? actionSurfaceTone.chipLightText : actionSurfaceTone.chipDarkText,
                      boxShadow: prefersLightMode ? "0 8px 18px rgba(15,23,42,0.10)" : "0 10px 22px rgba(2,6,23,0.28)",
                    }}
                  >
                    {videoActionPanelOpen === "share"
                      ? "Video Share Surface"
                      : videoActionPanelOpen === "frame"
                        ? "Frame Tools"
                        : videoActionPanelOpen === "export"
                          ? "Export Surface"
                          : "Publish Surface"}
                  </div>
                  <div className="mt-2 rounded-[16px] px-3 py-2" style={titleScrimStyle}>
                    <div className={`text-[13px] font-semibold ${prefersLightMode ? "text-slate-900/96" : "text-white/96"}`}>
                      {videoActionPanelOpen === "share"
                        ? "Send, open, or hand off the focused clip to downstream share workflows"
                        : videoActionPanelOpen === "frame"
                          ? "Capture the current video moment as an editorial reference"
                          : videoActionPanelOpen === "export"
                            ? "Prepare this focused video clip for export and delivery"
                            : "Stage a publish preview or emit a publish intent for downstream runtime wiring"}
                    </div>
                  </div>
                  <div className={`mt-2 text-[12px] leading-5 ${prefersLightMode ? "text-slate-700/86" : "text-white/76"}`}>
                    {videoActionPanelOpen === "share"
                      ? videoShareSummary || "No shareable clip identity is available yet."
                      : videoActionPanelOpen === "frame"
                        ? `Current frame window ${frameSummary}`
                        : videoActionPanelOpen === "export"
                          ? `Ready state ${status.toUpperCase()} · ${bindingSummary}`
                          : publishSummary}
                  </div>

                  {videoActionPanelOpen === "publish" ? (
                    <div className="mt-3">
                      <label className={`mb-2 block text-[10px] font-black uppercase tracking-[0.2em] ${prefersLightMode ? "text-slate-700/72" : "text-cyan-50/68"}`}>
                        Publish Note
                      </label>
                      <textarea
                        value={publishDraft}
                        onChange={(event) => {
                          setPublishDraft(event.target.value);
                        }}
                        rows={3}
                        placeholder="Summarize publish gate, delivery target, or handoff notes"
                        className={`w-full resize-none rounded-2xl border px-3 py-2 text-[12px] outline-none ${
                          prefersLightMode
                            ? "border-slate-300/50 bg-white/70 text-slate-900 placeholder:text-slate-500/70"
                            : "border-white/12 bg-slate-950/36 text-white/92 placeholder:text-white/36"
                        }`}
                      />
                      <AnnotationReviewList
                        annotations={activeAnnotationRecords}
                        prefersLightMode={prefersLightMode}
                        onSelectAnnotation={(annotation) => editAnnotationInSurface(annotation, "review_list")}
                      />
                    </div>
                  ) : null}

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {videoActionPanelPresence === "share" ? (
                      <>
                        <button
                          type="button"
                          onClick={() => void handleNativeShare()}
                          disabled={actionBusyId === "share-native"}
                          className="inline-flex h-8 items-center rounded-full px-3 text-[10px] font-black tracking-[0.18em]"
                          style={{
                            background: prefersLightMode ? "rgba(186,230,253,0.24)" : "rgba(8,145,178,0.16)",
                            border: prefersLightMode ? "1px solid rgba(14,165,233,0.18)" : "1px solid rgba(34,211,238,0.14)",
                            color: prefersLightMode ? "rgba(12,74,110,0.92)" : "rgba(165,243,252,0.88)",
                            opacity: actionBusyId === "share-native" ? 0.68 : 1,
                          }}
                        >
                          {actionBusyId === "share-native" ? "SHARING" : "SYSTEM SHARE"}
                        </button>
                        <button
                          type="button"
                          onClick={handleOpenClip}
                          className="inline-flex h-8 items-center rounded-full px-3 text-[10px] font-black tracking-[0.18em]"
                          style={{
                            background: prefersLightMode ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.08)",
                            border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
                            color: prefersLightMode ? "rgba(30,41,59,0.76)" : "rgba(255,255,255,0.74)",
                          }}
                        >
                          OPEN CLIP
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownloadClip("share")}
                          className="inline-flex h-8 items-center rounded-full px-3 text-[10px] font-black tracking-[0.18em]"
                          style={{
                            background: prefersLightMode ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.08)",
                            border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
                            color: prefersLightMode ? "rgba(30,41,59,0.76)" : "rgba(255,255,255,0.74)",
                          }}
                        >
                          DOWNLOAD CLIP
                        </button>
                      </>
                    ) : null}

                    {videoActionPanelPresence === "frame" ? (
                      <>
                        <button
                          type="button"
                          onClick={() => void handleCaptureFrame()}
                          disabled={actionBusyId === "frame-capture"}
                          className="inline-flex h-8 items-center rounded-full px-3 text-[10px] font-black tracking-[0.18em]"
                          style={{
                            background: prefersLightMode ? "rgba(186,230,253,0.24)" : "rgba(8,145,178,0.16)",
                            border: prefersLightMode ? "1px solid rgba(14,165,233,0.18)" : "1px solid rgba(34,211,238,0.14)",
                            color: prefersLightMode ? "rgba(12,74,110,0.92)" : "rgba(165,243,252,0.88)",
                            opacity: actionBusyId === "frame-capture" ? 0.68 : 1,
                          }}
                        >
                          {actionBusyId === "frame-capture" ? "CAPTURING" : "CAPTURE PNG"}
                        </button>
                        <button
                          type="button"
                          onClick={handleAddFrameMarker}
                          className="inline-flex h-8 items-center rounded-full px-3 text-[10px] font-black tracking-[0.18em]"
                          style={{
                            background: prefersLightMode ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.08)",
                            border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
                            color: prefersLightMode ? "rgba(30,41,59,0.76)" : "rgba(255,255,255,0.74)",
                          }}
                        >
                          ADD FRAME MARKER
                        </button>
                      </>
                    ) : null}

                    {videoActionPanelPresence === "export" ? (
                      <>
                        <button
                          type="button"
                          onClick={() => void handleExportPackage()}
                          disabled={actionBusyId === "export-package"}
                          className="inline-flex h-8 items-center rounded-full px-3 text-[10px] font-black tracking-[0.18em]"
                          style={{
                            background: prefersLightMode ? "rgba(186,230,253,0.24)" : "rgba(8,145,178,0.16)",
                            border: prefersLightMode ? "1px solid rgba(14,165,233,0.18)" : "1px solid rgba(34,211,238,0.14)",
                            color: prefersLightMode ? "rgba(12,74,110,0.92)" : "rgba(165,243,252,0.88)",
                            opacity: actionBusyId === "export-package" ? 0.68 : 1,
                          }}
                        >
                          {actionBusyId === "export-package" ? "EXPORTING" : "EXPORT JSON"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownloadClip("export")}
                          className="inline-flex h-8 items-center rounded-full px-3 text-[10px] font-black tracking-[0.18em]"
                          style={{
                            background: prefersLightMode ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.08)",
                            border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
                            color: prefersLightMode ? "rgba(30,41,59,0.76)" : "rgba(255,255,255,0.74)",
                          }}
                        >
                          DIRECT DOWNLOAD
                        </button>
                      </>
                    ) : null}

                    {videoActionPanelPresence === "publish" ? (
                      <>
                        <button
                          type="button"
                          onClick={handleStagePublishPreview}
                          className="inline-flex h-8 items-center rounded-full px-3 text-[10px] font-black tracking-[0.18em]"
                          style={{
                            background: prefersLightMode ? "rgba(186,230,253,0.24)" : "rgba(8,145,178,0.16)",
                            border: prefersLightMode ? "1px solid rgba(14,165,233,0.18)" : "1px solid rgba(34,211,238,0.14)",
                            color: prefersLightMode ? "rgba(12,74,110,0.92)" : "rgba(165,243,252,0.88)",
                          }}
                        >
                          STAGE PREVIEW
                        </button>
                        <button
                          type="button"
                          onClick={handlePublishClip}
                          className="inline-flex h-8 items-center rounded-full px-3 text-[10px] font-black tracking-[0.18em]"
                          style={{
                            background: prefersLightMode ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.08)",
                            border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
                            color: prefersLightMode ? "rgba(30,41,59,0.76)" : "rgba(255,255,255,0.74)",
                          }}
                        >
                          EMIT PUBLISH
                        </button>
                      </>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {!isArcLensPrimary ? (
        <div
          className="relative mt-3 min-h-0 flex-1 overflow-y-auto pr-1"
          style={{
            scrollbarGutter: "stable both-edges",
            overscrollBehavior: "contain",
          }}
        >
          <div className="flex items-center gap-2 overflow-x-auto overflow-y-hidden whitespace-nowrap pb-1 scrollbar-thin scrollbar-track-transparent">
            {(["source", "clip", "sequence"] as SonicsFocusScope[]).map((mode) => {
              const isActive = (auditionMode ?? focusScope) === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => onSetAuditionMode?.(mode)}
                  className="rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em]"
                  style={{
                    border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
                    background: isActive
                      ? prefersLightMode
                        ? "rgba(186,230,253,0.24)"
                        : "rgba(8,145,178,0.16)"
                      : prefersLightMode
                        ? "rgba(255,255,255,0.18)"
                        : "rgba(255,255,255,0.05)",
                    color: isActive
                      ? prefersLightMode
                        ? "rgba(12,74,110,0.92)"
                        : "rgba(165,243,252,0.9)"
                      : prefersLightMode
                        ? "rgba(51,65,85,0.78)"
                        : "rgba(255,255,255,0.66)",
                    opacity: onSetAuditionMode ? 1 : 0.72,
                  }}
                  disabled={!onSetAuditionMode}
                >
                  {mode}
                </button>
              );
            })}
            <div
              className="rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em]"
              style={{
                border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
                background: prefersLightMode ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.05)",
                color: prefersLightMode ? "rgba(51,65,85,0.78)" : "rgba(255,255,255,0.66)",
                flex: "0 0 auto",
              }}
            >
              {auditionMode ? `${focusScopeLabel(auditionMode)} audition` : `${mediaFocusLabel} audition`}
            </div>
            <div
              className="rounded-full px-3 py-1.5 text-[10px] font-semibold tracking-[0.08em]"
              style={{
                border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(186,230,253,0.14)",
                background: prefersLightMode
                  ? "linear-gradient(180deg, rgba(255,255,255,0.28), rgba(226,232,240,0.16))"
                  : "linear-gradient(180deg, rgba(255,255,255,0.12), rgba(8,145,178,0.08) 34%, rgba(15,23,42,0.24))",
                color: prefersLightMode ? "rgba(30,41,59,0.82)" : "rgba(224,242,254,0.84)",
                boxShadow: prefersLightMode
                  ? "inset 0 1px 0 rgba(255,255,255,0.52)"
                  : "inset 0 1px 0 rgba(255,255,255,0.08), 0 0 14px rgba(14,165,233,0.12)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                flex: "0 0 auto",
              }}
            >
              {modeSummary}
            </div>
            <div
              className="rounded-full px-3 py-1.5 text-[10px] font-semibold tracking-[0.08em]"
              style={{
                border: prefersLightMode ? "1px solid rgba(14,165,233,0.18)" : "1px solid rgba(34,211,238,0.14)",
                background: prefersLightMode ? "rgba(186,230,253,0.16)" : "rgba(8,145,178,0.10)",
                color: prefersLightMode ? "rgba(12,74,110,0.88)" : "rgba(165,243,252,0.84)",
                flex: "0 0 auto",
              }}
            >
              {bindingSummary}
            </div>
          </div>
        </div>
      ) : null}

      <div className="sr-only">
        {focusScopeLabel(focusScope)}. Active source {activeSourceLabel || activeSourceId || "none"}. Active clip {activeClipLabel || activeClipId || "none"}.
        {activeSequenceLabel ? ` Active sequence ${activeSequenceLabel}.` : ""}
      </div>
    </div>
  );
}

export function ClipFocusLensOverlay({
  visible,
  activeLensId = null,
  pinnedMiniLensId = null,
  focusScope,
  status,
  currentTimeSec,
  durationSec,
  activeSourceId,
  activeClipId,
  placement = "stage",
}: ClipFocusLensOverlayProps): JSX.Element | null {
  const prefersLightMode = usePrefersLightMode();
  const entered = usePresenceMotion(visible && !!activeLensId);
  const mountedRef = useMountedRef();
  const [displayLensId, setDisplayLensId] = React.useState<SonicsLensId | null>(activeLensId);
  const [contentEntered, setContentEntered] = React.useState(false);
  const activeFocusEntity = activeClipId || activeSourceId || "No active focus";

  React.useEffect(() => {
    if (!visible || !activeLensId) {
      setContentEntered(false);
      setDisplayLensId(null);
      return;
    }

    setDisplayLensId(activeLensId);
    setContentEntered(false);
    const frame = window.requestAnimationFrame(() => {
      if (!mountedRef.current) return;
      setContentEntered(true);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeLensId, mountedRef, visible]);

  const activeLens = lensMeta(displayLensId);
  const isPinnedToCurrentLens = !!displayLensId && displayLensId === pinnedMiniLensId;

  if (!visible || !activeLens) return null;

  const overlayClassName =
    placement === "aboveBar"
      ? "pointer-events-none absolute inset-x-0 bottom-[calc(100%+10px)] z-[58]"
      : "pointer-events-none absolute inset-x-4 top-3 z-[58]";

  const panelClassName = placement === "aboveBar" ? "relative w-full max-w-[620px]" : "relative w-full max-w-[720px]";

  return (
    <div className={overlayClassName}>
      <div
        className={`${panelClassName} overflow-hidden rounded-[22px] border px-4 py-3`}
        style={{
          borderColor: activeLens.border,
          background: prefersLightMode
            ? `linear-gradient(180deg, rgba(248,250,252,0.28), ${activeLens.tint} 18%, rgba(226,232,240,0.18) 52%, rgba(241,245,249,0.12))`
            : `linear-gradient(180deg, rgba(15,23,42,0.7), ${activeLens.glow} 22%, rgba(15,23,42,0.42) 52%, rgba(15,23,42,0.22))`,
          boxShadow: prefersLightMode
            ? `inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -1px 0 rgba(255,255,255,0.08), 0 14px 30px rgba(148,163,184,0.12), 0 10px 24px ${activeLens.glow}, 0 0 0 1px ${activeLens.border}`
            : `inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(255,255,255,0.02), 0 14px 30px ${activeLens.glow}`,
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          opacity: entered ? 1 : 0,
          transform:
            placement === "aboveBar"
              ? entered
                ? "translateY(0px)"
                : "translateY(10px)"
              : entered
                ? "translateY(0px)"
                : "translateY(8px)",
          transition: "opacity 180ms ease, transform 220ms ease",
        }}
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-80"
          aria-hidden
          style={{
            background: prefersLightMode
              ? `linear-gradient(110deg, rgba(255,255,255,0.12), ${activeLens.sheen} 24%, rgba(255,255,255,0.03) 48%, rgba(255,255,255,0.00) 72%)`
              : "linear-gradient(110deg, rgba(255,255,255,0.04), rgba(255,255,255,0.015) 38%, rgba(255,255,255,0.00) 72%)",
          }}
        />

        {prefersLightMode ? (
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            aria-hidden
            style={{
              background: `linear-gradient(90deg, rgba(255,255,255,0), ${activeLens.sheen}, ${activeLens.accent}, rgba(255,255,255,0))`,
            }}
          />
        ) : null}

        <div
          className="relative flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
          style={{
            opacity: contentEntered ? 1 : 0.45,
            transform: contentEntered ? "translateY(0px)" : "translateY(4px)",
            transition: "opacity 160ms ease, transform 180ms ease",
          }}
        >
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="inline-flex h-7 items-center rounded-full px-3 text-[10px] font-black tracking-[0.22em]"
                style={{
                  background: activeLens.glow,
                  border: `1px solid ${activeLens.border}`,
                  color: activeLens.accent,
                }}
              >
                {activeLens.label}
              </span>
              <span className={`text-[13px] font-semibold ${prefersLightMode ? "text-slate-900/92" : "text-white/94"}`}>
                {activeLens.title}
              </span>
              <span className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${prefersLightMode ? "text-slate-600/70" : "text-white/48"}`}>
                {focusScopeLabel(focusScope)}
              </span>
              {isPinnedToCurrentLens ? (
                <span
                  className="inline-flex h-6 items-center rounded-full px-2.5 text-[9px] font-black tracking-[0.18em]"
                  style={{
                    border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.10)",
                    background: prefersLightMode ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.05)",
                    color: prefersLightMode ? "rgba(51,65,85,0.78)" : "rgba(255,255,255,0.74)",
                  }}
                >
                  PINNED
                </span>
              ) : null}
            </div>

            <div className={`mt-2 max-w-[720px] text-[13px] leading-6 ${prefersLightMode ? "text-slate-700/84" : "text-white/74"}`}>
              {activeLens.summary}
            </div>

            <div className={`mt-3 flex flex-wrap items-center gap-2 text-[11px] ${prefersLightMode ? "text-slate-700/74" : "text-white/62"}`}>
              <span
                className="rounded-full px-3 py-1"
                style={{
                  border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
                  background: prefersLightMode ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.045)",
                }}
              >
                {fmt(currentTimeSec)} / {fmt(durationSec)}
              </span>
              <span
                className="rounded-full px-3 py-1 uppercase tracking-[0.14em]"
                style={{
                  border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
                  background: prefersLightMode ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.045)",
                }}
              >
                {status}
              </span>
              <span
                className="rounded-full px-3 py-1"
                style={{
                  border: prefersLightMode ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(255,255,255,0.08)",
                  background: prefersLightMode ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.045)",
                }}
              >
                {activeFocusEntity}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-start gap-2 lg:max-w-[360px] lg:justify-end">
            {activeLens.pills.map((pill) => (
              <span
                key={pill}
                className="inline-flex h-8 items-center rounded-full px-3 text-[10px] font-black tracking-[0.18em]"
                style={{
                  background: activeLens.glow,
                  border: `1px solid ${activeLens.border}`,
                  color: activeLens.accent,
                }}
              >
                {pill}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function normalizeAnnotationHandoffKind(kind: unknown): AnnotationSurfaceKind {
  const candidate = typeof kind === "string" ? kind.trim().toLowerCase() : "";
  return VALID_ANNOTATION_KINDS.includes(candidate as AnnotationSurfaceKind)
    ? (candidate as AnnotationSurfaceKind)
    : "intent";
}